// Bilingual translation dictionary + structured data for Rota Yachting
// Languages: EN (English), RU (Russian)

export const LANGUAGES = ["en", "ru", "tr"];

export const ui = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      destinations: "Destinations",
      news: "Insights",
      contact: "Contact",
      requestSupport: "Request Support",
    },
    common: {
      readMore: "Read more",
      learnMore: "Learn more",
      viewService: "View service",
      viewAll: "View all",
      backTo: "Back to",
      allServices: "All services",
      allDestinations: "All destinations",
      allArticles: "All insights",
      contactUs: "Contact us",
      requestService: "Request this service",
      whatWeHandle: "What we handle",
      processSteps: "Our process",
      whyItMatters: "Why it matters",
      relatedServices: "Related services",
      overview: "Overview",
      getInTouch: "Get in touch",
    },
    hero: {
      eyebrow: "Registered Yacht Agency · Türkiye",
      title: "Beyond the Ordinary",
      titleLine2: "Yacht Agency Services in Türkiye",
      subtitle:
        "Operational excellence for captains, crew and yacht managers. Port clearance, berth coordination, customs, provisioning and concierge — delivered with the discretion your yacht demands.",
      ctaPrimary: "Request Support",
      ctaSecondary: "Our Services",
    },
    stats: {
      operations: "Operations handled",
      years: "Years of maritime heritage",
      ports: "Ports covered in Türkiye",
      support: "Operational support",
    },
    home: {
      servicesTitle: "A complete agency, at every port",
      servicesSubtitle:
        "From the moment your yacht approaches Turkish waters to the moment she departs — we handle every detail, on the ground, with local authority and international standards.",
      whyTitle: "Why yacht captains choose Rota",
      whySubtitle:
        "We are not a broker. We are the silent operational partner that makes complex voyages look effortless.",
      whyPoints: [
        {
          title: "Local authority, global standards",
          body: "Three decades of maritime agency heritage in Türkiye, applied exclusively to yachts with yacht-specific protocols.",
        },
        {
          title: "One point of contact",
          body: "A dedicated agent coordinates every stakeholder — port authorities, customs, marinas, suppliers — so captains brief once.",
        },
        {
          title: "24/7 operational mindset",
          body: "Yachting does not keep office hours. Neither do we. Weekend arrivals, late-night spare parts, dawn clearances — handled.",
        },
        {
          title: "Discretion as standard",
          body: "We serve private owners, charter fleets and management companies who expect confidentiality as a baseline, not a premium.",
        },
      ],
      coverageTitle: "Turkish waters, fully covered",
      coverageSubtitle:
        "From the Bosphorus to the Mediterranean — a unified agency network across Türkiye's most requested yachting destinations.",
      trustTitle: "Trusted by captains, managers and owners",
      trustSubtitle:
        "Private yachts, charter operators and management companies rely on Rota to quietly execute what matters on the water.",
      testimonialsEyebrow: "Voices from the bridge",
      testimonialsTitle: "What captains say",
      testimonialsSubtitle:
        "Selected feedback from captains and yacht managers we've supported across Turkish waters. Names withheld for privacy.",
      testimonials: [
        {
          quote: "Rota handled our Sinop-to-Bodrum transit clearance like clockwork. Berths confirmed before we arrived, customs paperwork ready at the gangway. Quiet, precise, professional.",
          role: "Captain · 52m Motor Yacht",
          flag: "Cayman Islands flag",
        },
        {
          quote: "We rely on Rota every season for Bosphorus transits and East-Med provisioning. Their network in Türkiye is unmatched and they treat owner sensitivities with absolute discretion.",
          role: "Fleet Manager · MYBA Member",
          flag: "European management company",
        },
        {
          quote: "First time bringing the yacht to Türkiye and Rota briefed us a week in advance — every port, every contact, every cost. We never had to ask twice.",
          role: "Owner's Representative",
          flag: "70m Superyacht · Marshall Islands",
        },
      ],
      quickContactTitle: "Arriving in Türkiye?",
      quickContactBody:
        "Send us your ETA and we'll prepare clearance, berth and provisioning — before you drop anchor.",
    },
    about: {
      eyebrow: "About Rota Yachting",
      title: "A maritime heritage, dedicated to yachting",
      intro:
        "Rota Yachting is the yacht-dedicated division of Rota Denizcilik — a Turkish maritime agency founded in Sinop in 1990. For more than three decades, our group has served vessels across more than thirty Turkish ports. Rota Yachting applies that institutional depth exclusively to private and charter yachts.",
      sections: [
        {
          h: "Who we are",
          p: "A full-service yacht agency built on an established Turkish maritime group. We do not sell or charter yachts. We exist to make the operational side of yachting in Türkiye invisible to the guest and effortless for the captain.",
        },
        {
          h: "Our maritime heritage",
          p: "Founded by the Levend family in Sinop, our parent agency has handled more than 450,000 maritime operations for shipping lines, cruise operators and private vessels. That operational muscle — relationships with port authorities, customs, marinas and suppliers — is what makes Rota Yachting quietly effective.",
        },
        {
          h: "Our approach",
          p: "We work in the background. One dedicated agent per yacht. One point of contact. Pre-arrival planning, in-port execution, post-departure follow-up. Every call is answered. Every document is prepared. Every request is anticipated.",
        },
        {
          h: "Why clients trust us",
          p: "We are licensed, insured and officially registered as a Turkish ship agency. Our paperwork is precise. Our response times are fast. Our discretion is absolute. And our local knowledge — built over thirty-five years — cannot be improvised.",
        },
      ],
      valuesTitle: "What we stand for",
      values: [
        { h: "Precision", p: "In paperwork, timing and logistics. Yachting leaves no room for approximation." },
        { h: "Discretion", p: "Our clients' privacy is protected by default, not by request." },
        { h: "Availability", p: "A real human voice, in English or Russian, at any hour." },
        { h: "Integrity", p: "Transparent disbursement accounts and honest advice — always." },
      ],
    },
    servicesPage: {
      eyebrow: "Full-service Yacht Agency",
      title: "Agency services for yachts in Türkiye",
      intro:
        "Nine core services, one dedicated agent. Every aspect of operational support — from clearance to concierge — handled in-house with local authority.",
      ctaEyebrow: "Operations Desk · 24 / 7",
      ctaTitle: "Before the lines come ashore — everything is already in place.",
      ctaBody: "Berths confirmed. Customs notified. Pilot booked. Provisions waiting on the dock. Crew transfers timed to the minute. The mark of a serious agent is not what happens at the gangway — it is what was already done before you arrived.",
      ctaPrimary: "Brief us on your yacht",
      ctaSecondary: "+90 532 472 92 68",
      ctaWhatsapp: "WhatsApp the duty agent",
    },
    destinationsPage: {
      eyebrow: "Coverage",
      title: "Turkish waters, port by port",
      intro:
        "From the gateway to the Black Sea to the pristine bays of the Lycian coast — a unified agency presence across Türkiye's most requested yachting destinations.",
    },
    newsPage: {
      eyebrow: "Insights",
      title: "Yachting in Türkiye — briefings & guides",
      intro:
        "Operational updates, cruising notes and regulatory briefings for captains, yacht managers and owners planning Turkish waters.",
    },
    contactPage: {
      eyebrow: "Contact",
      title: "Let's prepare your arrival",
      intro:
        "Send your ETA, yacht details and service needs. We'll reply within operational hours — typically under an hour during the season.",
      formTitle: "Request operational support",
      offices: "Our offices",
      direct: "Direct contacts",
      headOffice: "Head Office",
      branchIstanbul: "Branch Office · İstanbul",
      branchAmasra: "Branch Office · Amasra",
      registration: "Company Registration",
    },
    form: {
      name: "Full name",
      email: "Email",
      phone: "Phone",
      yacht: "Yacht name",
      location: "Location / port",
      service: "Service needed",
      servicePlaceholder: "Select a service",
      message: "Message",
      messagePlaceholder: "ETA, flag, LOA, crew count, specific requirements...",
      submit: "Send inquiry",
      submitting: "Sending...",
      success: "Thank you. Our agency team will respond shortly.",
      error: "Something went wrong. Please try again or WhatsApp us.",
      required: "required",
    },
    footer: {
      tagline: "Beyond the Ordinary yacht agency services in Türkiye.",
      taglineLead: "Beyond the Ordinary",
      taglineScript: "beyond the ordinary",
      taglineCoda: "Türkiye · Sinop HQ · Est. 1990",
      services: "Services",
      company: "Company",
      legal: "Legal",
      coverage: "Coverage",
      whatsapp: "WhatsApp",
      email: "Email",
      phone: "Phone",
      rights: "All rights reserved.",
      disclaimer: "Rota Yachting is the yacht agency division of Rota Denizcilik Turizm ve Dış Ticaret A.Ş.",
    },
    whatsapp: "Chat on WhatsApp",
  },
  ru: {
    nav: {
      home: "Главная",
      about: "О нас",
      services: "Услуги",
      destinations: "Направления",
      news: "Аналитика",
      contact: "Контакты",
      requestSupport: "Запросить поддержку",
    },
    common: {
      readMore: "Подробнее",
      learnMore: "Узнать больше",
      viewService: "К услуге",
      viewAll: "Смотреть все",
      backTo: "Назад к",
      allServices: "Все услуги",
      allDestinations: "Все направления",
      allArticles: "Все материалы",
      contactUs: "Связаться",
      requestService: "Запросить услугу",
      whatWeHandle: "Что мы берём на себя",
      processSteps: "Наш процесс",
      whyItMatters: "Почему это важно",
      relatedServices: "Смежные услуги",
      overview: "Обзор",
      getInTouch: "Связаться с нами",
    },
    hero: {
      eyebrow: "Зарегистрированное яхтенное агентство · Турция",
      title: "Beyond the Ordinary",
      titleLine2: "Яхтенное агентирование в Турции",
      subtitle:
        "Операционное мастерство для капитанов, экипажей и яхт-менеджеров. Портовое оформление, резервирование стоянок, таможня, снабжение и консьерж — с деликатностью, которую требует ваша яхта.",
      ctaPrimary: "Запросить поддержку",
      ctaSecondary: "Наши услуги",
    },
    stats: {
      operations: "Проведённых операций",
      years: "Лет морского наследия",
      ports: "Портов Турции в покрытии",
      support: "Операционная поддержка",
    },
    home: {
      servicesTitle: "Полное агентирование — в каждом порту",
      servicesSubtitle:
        "С момента подхода вашей яхты к турецким водам до её отхода — мы берём на себя каждую деталь на местах, с местным авторитетом и международными стандартами.",
      whyTitle: "Почему капитаны выбирают Rota",
      whySubtitle:
        "Мы не брокер. Мы тихий операционный партнёр, который делает сложные переходы незаметными.",
      whyPoints: [
        {
          title: "Местный авторитет, глобальные стандарты",
          body: "Три десятилетия морского агентского опыта в Турции, применённые исключительно к яхтам — с яхтенными протоколами.",
        },
        {
          title: "Одна точка контакта",
          body: "Выделенный агент координирует все службы — портовые власти, таможню, марины, поставщиков — чтобы капитан давал инструкции один раз.",
        },
        {
          title: "Режим 24/7",
          body: "Яхтинг не знает рабочих часов. Мы тоже. Прибытие в выходной, запчасть ночью, оформление на рассвете — всё решается.",
        },
        {
          title: "Конфиденциальность по умолчанию",
          body: "Мы работаем с частными владельцами, чартерными флотами и управляющими компаниями. Конфиденциальность — базовый стандарт, не опция.",
        },
      ],
      coverageTitle: "Турецкие воды — без белых пятен",
      coverageSubtitle:
        "От Босфора до Средиземноморья — единая агентская сеть по самым востребованным яхтенным направлениям Турции.",
      trustTitle: "Нам доверяют капитаны, менеджеры и владельцы",
      trustSubtitle:
        "Частные яхты, чартерные операторы и управляющие компании полагаются на Rota, чтобы тихо исполнить важное на воде.",
      testimonialsEyebrow: "Голоса с мостика",
      testimonialsTitle: "Что говорят капитаны",
      testimonialsSubtitle:
        "Избранные отзывы капитанов и менеджеров, которым мы помогли в турецких водах. Имена скрыты в целях конфиденциальности.",
      testimonials: [
        {
          quote: "Rota провели транзит из Синопа в Бодрум как по нотам. Стоянки подтверждены до прибытия, таможенные документы готовы у трапа. Тихо, точно, профессионально.",
          role: "Капитан · моторная яхта 52 м",
          flag: "Флаг Каймановых островов",
        },
        {
          quote: "Каждый сезон мы доверяем Rota транзит через Босфор и снабжение в Восточном Средиземноморье. Их сеть в Турции не имеет равных, а к интересам владельцев они относятся с абсолютной деликатностью.",
          role: "Флит-менеджер · член MYBA",
          flag: "Европейская управляющая компания",
        },
        {
          quote: "Впервые привели яхту в Турцию — Rota за неделю прислали полный бриф: каждый порт, каждый контакт, каждая стоимость. Дважды переспрашивать не пришлось ни разу.",
          role: "Представитель владельца",
          flag: "Суперъяхта 70 м · Маршалловы Острова",
        },
      ],
      quickContactTitle: "Прибываете в Турцию?",
      quickContactBody:
        "Пришлите нам ETA — мы подготовим оформление, стоянку и снабжение до того, как вы встанете на якорь.",
    },
    about: {
      eyebrow: "О Rota Yachting",
      title: "Морское наследие, посвящённое яхтингу",
      intro:
        "Rota Yachting — яхтенное подразделение Rota Denizcilik, турецкого морского агентства, основанного в Синопе в 1990 году. Более тридцати лет наша группа обслуживает суда в более чем тридцати турецких портах. Rota Yachting применяет этот институциональный опыт исключительно к частным и чартерным яхтам.",
      sections: [
        {
          h: "Кто мы",
          p: "Полноценное яхтенное агентство, созданное на базе состоявшейся турецкой морской группы. Мы не продаём и не сдаём яхты в чартер. Мы существуем, чтобы сделать операционную сторону яхтинга в Турции незаметной для гостя и простой для капитана.",
        },
        {
          h: "Наше морское наследие",
          p: "Основанное семьёй Левенд в Синопе, наше материнское агентство провело более 450 000 морских операций для линий, круизных операторов и частных судов. Этот операционный вес — отношения с портовыми властями, таможней, маринами и поставщиками — делает Rota Yachting тихо эффективной.",
        },
        {
          h: "Наш подход",
          p: "Мы работаем за кадром. Один выделенный агент на яхту. Одна точка контакта. Планирование до прибытия, исполнение в порту, сопровождение после отхода. Каждый звонок принят. Каждый документ подготовлен. Каждая просьба предвосхищена.",
        },
        {
          h: "Почему нам доверяют",
          p: "Мы лицензированы, застрахованы и официально зарегистрированы как турецкое судовое агентство. Документы точны. Отклик быстр. Конфиденциальность абсолютна. А местное знание, накопленное за тридцать пять лет, нельзя импровизировать.",
        },
      ],
      valuesTitle: "На чём мы стоим",
      values: [
        { h: "Точность", p: "В документах, сроках и логистике. Яхтинг не прощает приблизительности." },
        { h: "Конфиденциальность", p: "Приватность клиента защищена по умолчанию, не по запросу." },
        { h: "Доступность", p: "Живой голос, на английском или русском, в любой час." },
        { h: "Честность", p: "Прозрачные счета расходов и честный совет — всегда." },
      ],
    },
    servicesPage: {
      eyebrow: "Полный спектр агентирования",
      title: "Яхтенные агентские услуги в Турции",
      intro:
        "Девять ключевых услуг, один выделенный агент. Вся операционная поддержка — от оформления до консьержа — исполняется собственными силами с местным авторитетом.",
      ctaEyebrow: "Дежурный отдел · 24 / 7",
      ctaTitle: "До того как швартовы окажутся на причале — всё уже на местах.",
      ctaBody: "Стоянки подтверждены. Таможня предупреждена. Лоцман зарезервирован. Провиант ждёт у причала. Трансферы экипажа выверены до минуты. Признак серьёзного агента — не то, что происходит у трапа, а то, что уже было сделано до вашего прихода.",
      ctaPrimary: "Расскажите о вашей яхте",
      ctaSecondary: "+90 532 472 92 68",
      ctaWhatsapp: "WhatsApp дежурному агенту",
    },
    destinationsPage: {
      eyebrow: "Покрытие",
      title: "Турецкие воды, порт за портом",
      intro:
        "От ворот Чёрного моря до нетронутых бухт Ликийского побережья — единое агентское присутствие в самых востребованных яхтенных направлениях Турции.",
    },
    newsPage: {
      eyebrow: "Аналитика",
      title: "Яхтинг в Турции — брифы и руководства",
      intro:
        "Операционные обновления, заметки о круизах и регуляторные брифы для капитанов, яхт-менеджеров и владельцев, планирующих турецкие воды.",
    },
    contactPage: {
      eyebrow: "Контакты",
      title: "Подготовим ваше прибытие",
      intro:
        "Пришлите ETA, данные яхты и перечень услуг. Мы ответим в рабочее время — обычно в течение часа в сезон.",
      formTitle: "Запрос на операционную поддержку",
      offices: "Наши офисы",
      direct: "Прямые контакты",
      headOffice: "Головной офис",
      branchIstanbul: "Филиал · Стамбул",
      branchAmasra: "Филиал · Амасра",
      registration: "Реквизиты компании",
    },
    form: {
      name: "Полное имя",
      email: "Электронная почта",
      phone: "Телефон",
      yacht: "Название яхты",
      location: "Локация / порт",
      service: "Требуемая услуга",
      servicePlaceholder: "Выберите услугу",
      message: "Сообщение",
      messagePlaceholder: "ETA, флаг, LOA, численность экипажа, особые требования...",
      submit: "Отправить запрос",
      submitting: "Отправка...",
      success: "Благодарим. Команда агентства ответит в ближайшее время.",
      error: "Что-то пошло не так. Пожалуйста, попробуйте снова или напишите в WhatsApp.",
      required: "обязательно",
    },
    footer: {
      tagline: "Beyond the Ordinary — яхтенное агентирование в Турции.",
      taglineLead: "Beyond the Ordinary",
      taglineScript: "beyond the ordinary",
      taglineCoda: "Турция · Штаб Синоп · Осн. 1990",
      services: "Услуги",
      company: "Компания",
      legal: "Юридическое",
      coverage: "Покрытие",
      whatsapp: "WhatsApp",
      email: "Email",
      phone: "Телефон",
      rights: "Все права защищены.",
      disclaimer: "Rota Yachting — яхтенное подразделение Rota Denizcilik Turizm ve Dış Ticaret A.Ş.",
    },
    whatsapp: "Написать в WhatsApp",
  },
  tr: {
    nav: {
      home: "Ana Sayfa",
      about: "Hakkımızda",
      services: "Hizmetler",
      destinations: "Limanlar",
      news: "Yazılar",
      contact: "İletişim",
      requestSupport: "Destek Talebi",
    },
    common: {
      readMore: "Devamını oku",
      learnMore: "Daha fazlası",
      viewService: "Hizmete git",
      viewAll: "Tümünü gör",
      backTo: "Geri",
      allServices: "Tüm hizmetler",
      allDestinations: "Tüm limanlar",
      allArticles: "Tüm yazılar",
      contactUs: "Bize ulaşın",
      requestService: "Bu hizmeti talep edin",
      whatWeHandle: "Üstlendiklerimiz",
      processSteps: "İşleyişimiz",
      whyItMatters: "Neden önemli",
      relatedServices: "İlgili hizmetler",
      overview: "Genel bakış",
      getInTouch: "İletişime geçin",
    },
    hero: {
      eyebrow: "Tescilli Yat Acentesi · Türkiye",
      title: "Beyond the Ordinary",
      titleLine2: "Türkiye'de Yat Acentecilik",
      subtitle:
        "Kaptanlar, mürettebat ve yat yöneticileri için operasyonel mükemmellik. Liman çıkışı, yer ayırtma, gümrük, ikmal ve concierge — yatınızın hak ettiği titizlikle.",
      ctaPrimary: "Destek Talep Edin",
      ctaSecondary: "Hizmetlerimiz",
    },
    stats: {
      operations: "Tamamlanan operasyon",
      years: "Yıllık denizcilik mirası",
      ports: "Türkiye'de hizmet verilen liman",
      support: "Operasyonel destek",
    },
    home: {
      servicesTitle: "Her limanda eksiksiz acente",
      servicesSubtitle:
        "Yatınızın Türk sularına yaklaşmasıyla ayrılışı arasındaki her detayı, sahada, yerel otoriteyle ve uluslararası standartlarla biz yönetiyoruz.",
      whyTitle: "Kaptanlar neden Rota'yı seçiyor?",
      whySubtitle:
        "Biz broker değiliz. Karmaşık seyirleri zahmetsiz gösteren sessiz operasyonel ortağız.",
      whyPoints: [
        {
          title: "Yerel otorite, küresel standart",
          body: "Türkiye'de otuz yılı aşkın denizcilik acenteliği mirasını yatlara özel protokollerle sadece yatlara uyguluyoruz.",
        },
        {
          title: "Tek temas noktası",
          body: "Atanmış bir acente; liman idaresi, gümrük, marina ve tedarikçileri koordine eder. Kaptan tek seferde brifing verir.",
        },
        {
          title: "7/24 operasyonel zihniyet",
          body: "Yatçılığın mesai saati yoktur; bizim de. Hafta sonu yanaşması, gece yedek parça, şafak çıkışı — hepsi halledilir.",
        },
        {
          title: "Standart olarak gizlilik",
          body: "Özel armatörler, charter filolar ve yönetim şirketleriyle çalışırız. Gizlilik bizde opsiyon değil, başlangıç noktasıdır.",
        },
      ],
      coverageTitle: "Türk suları, kapsamlı şekilde",
      coverageSubtitle:
        "Boğaz'dan Akdeniz'e — Türkiye'nin en çok talep gören yat destinasyonlarında birleşik bir acente ağı.",
      trustTitle: "Kaptanlar, yöneticiler ve armatörler bize güveniyor",
      trustSubtitle:
        "Özel yatlar, charter operatörleri ve yönetim şirketleri Rota'ya su üstündeki kritik işlerin sessizce icrası için güveniyor.",
      testimonialsEyebrow: "Köprü üstünden sesler",
      testimonialsTitle: "Kaptanlar ne diyor",
      testimonialsSubtitle:
        "Türk sularında destek verdiğimiz kaptan ve yat yöneticilerinden seçilmiş geri bildirimler. Gizlilik gereği isimler paylaşılmamıştır.",
      testimonials: [
        {
          quote: "Rota, Sinop'tan Bodrum'a geçişimizdeki tüm acente işlemlerini saat gibi yürüttü. Yer tahsisi gemi yanaşmadan önce hazır, gümrük evrakları iskelede bekliyordu. Sessiz, kesin ve profesyonel.",
          role: "Kaptan · 52 m motor yat",
          flag: "Cayman bayraklı",
        },
        {
          quote: "Her sezon Boğaz transit ve Doğu Akdeniz ikmali için Rota'ya güveniyoruz. Türkiye'deki ağları benzersiz ve armatör hassasiyetlerine mutlak bir gizlilikle yaklaşıyorlar.",
          role: "Filo Müdürü · MYBA üyesi",
          flag: "Avrupalı yönetim şirketi",
        },
        {
          quote: "Yatı ilk kez Türkiye'ye getirdik. Rota bir hafta öncesinden bizi her detayıyla brifledi — her liman, her kontak, her maliyet. İkinci kez sormamıza gerek kalmadı.",
          role: "Armatör Temsilcisi",
          flag: "70 m süperyat · Marshall Adaları",
        },
      ],
      quickContactTitle: "Türkiye'ye mi geliyorsunuz?",
      quickContactBody:
        "ETA'nızı bize iletin; demir atmadan önce çıkış işlemleri, yer ve ikmal hazır olsun.",
    },
    about: {
      eyebrow: "Rota Yachting Hakkında",
      title: "Yatçılığa adanmış bir denizcilik mirası",
      intro:
        "Rota Yachting, 1990'da Sinop'ta kurulan Rota Denizcilik bünyesinde, yatlara özel oluşturulmuş acente birimidir. Otuz yılı aşkın süredir grubumuz Türkiye'nin otuzdan fazla limanında gemilere hizmet veriyor. Rota Yachting bu kurumsal derinliği yalnızca özel ve charter yatlara uyarlar.",
      sections: [
        {
          h: "Biz kimiz",
          p: "Köklü bir Türk denizcilik grubu üzerine inşa edilmiş tam kapsamlı bir yat acentesiyiz. Yat satmıyor, yat kiralamıyoruz. Türkiye'de yatçılığın operasyonel tarafını konuk için görünmez, kaptan için zahmetsiz kılmak için varız.",
        },
        {
          h: "Denizcilik mirasımız",
          p: "Sinop'ta Levend ailesi tarafından kurulan ana acentemiz; armatörlük şirketleri, kruvaziyer operatörleri ve özel gemiler için 450.000'den fazla denizcilik operasyonu yürütmüştür. Liman idareleri, gümrük, marinalar ve tedarikçilerle kurulan bu ağ, Rota Yachting'in sessiz etkinliğinin temelidir.",
        },
        {
          h: "Yaklaşımımız",
          p: "Sahne arkasında çalışırız. Her yata atanmış bir acente. Tek temas noktası. Geliş öncesi planlama, limanda icra, ayrılış sonrası takip. Her arama yanıtlanır. Her belge hazırlanır. Her talep önceden öngörülür.",
        },
        {
          h: "Müşteriler neden bize güveniyor",
          p: "Türk gemi acentesi olarak lisanslı, sigortalı ve resmi olarak kayıtlıyız. Belgelerimiz kusursuz. Yanıt sürelerimiz hızlı. Gizliliğimiz mutlaktır. Otuz beş yıllık yerel bilgi ise doğaçlanamaz.",
        },
      ],
      valuesTitle: "Neye inanıyoruz",
      values: [
        { h: "Hassasiyet", p: "Belgede, zamanlamada ve lojistikte. Yatçılık yaklaşıklık kabul etmez." },
        { h: "Gizlilik", p: "Müşteri mahremiyeti talep değil, varsayılandır." },
        { h: "Erişilebilirlik", p: "İngilizce veya Rusça gerçek bir insan sesi, her saatte." },
        { h: "Dürüstlük", p: "Şeffaf disbursement hesapları ve her zaman dürüst tavsiye." },
      ],
    },
    servicesPage: {
      eyebrow: "Tam Kapsamlı Yat Acentesi",
      title: "Türkiye'de yatlara acente hizmetleri",
      intro:
        "Dokuz temel hizmet, tek atanmış acente. Çıkış işlemlerinden concierge'e kadar tüm operasyonel destek — yerel otoriteyle, kurum içinde.",
      ctaEyebrow: "Operasyon Masası · 7 / 24",
      ctaTitle: "Halatlar henüz iskeleye çıkmadan — her şey çoktan yerinde.",
      ctaBody: "Yer tahsisi onaylandı. Gümrük bilgilendirildi. Kılavuz kaptan rezervasyonu yapıldı. İkmal iskelede bekliyor. Mürettebat transferleri dakikası dakikasına ayarlandı. Ciddi bir acentenin alameti, iskelede yaşanan değil, siz varmadan önce çoktan tamamlanmış olan iştir.",
      ctaPrimary: "Yatınızı bize anlatın",
      ctaSecondary: "+90 532 472 92 68",
      ctaWhatsapp: "Nöbetçi acenteye WhatsApp",
    },
    destinationsPage: {
      eyebrow: "Kapsam",
      title: "Türk suları, liman liman",
      intro:
        "Karadeniz kapısından Likya kıyılarının el değmemiş koylarına — Türkiye'nin en çok talep gören yat destinasyonlarında birleşik bir acente varlığı.",
    },
    newsPage: {
      eyebrow: "Yazılar",
      title: "Türkiye'de yatçılık — bilgi notları ve rehberler",
      intro:
        "Türk sularını planlayan kaptanlar, yat yöneticileri ve armatörler için operasyonel güncellemeler, seyir notları ve mevzuat brifingleri.",
    },
    contactPage: {
      eyebrow: "İletişim",
      title: "Gelişinizi birlikte hazırlayalım",
      intro:
        "ETA'nızı, yat detaylarınızı ve hizmet ihtiyaçlarınızı paylaşın. Sezonda genellikle bir saat içinde dönüş yaparız.",
      formTitle: "Operasyonel destek talebi",
      offices: "Ofislerimiz",
      direct: "Doğrudan iletişim",
      headOffice: "Genel Merkez",
      branchIstanbul: "Şube · İstanbul",
      branchAmasra: "Şube · Amasra",
      registration: "Şirket Bilgileri",
    },
    form: {
      name: "Ad Soyad",
      email: "E-posta",
      phone: "Telefon",
      yacht: "Yat adı",
      location: "Konum / liman",
      service: "Talep edilen hizmet",
      servicePlaceholder: "Bir hizmet seçin",
      message: "Mesaj",
      messagePlaceholder: "ETA, bayrak, LOA, mürettebat sayısı, özel istekler...",
      submit: "Talebi gönder",
      submitting: "Gönderiliyor...",
      success: "Teşekkürler. Acente ekibimiz kısa süre içinde dönüş yapacak.",
      error: "Bir şeyler ters gitti. Lütfen tekrar deneyin veya WhatsApp'tan yazın.",
      required: "zorunlu",
    },
    footer: {
      tagline: "Türkiye'de Beyond the Ordinary yat acente hizmetleri.",
      taglineLead: "Beyond the Ordinary",
      taglineScript: "beyond the ordinary",
      taglineCoda: "Türkiye · Sinop Merkez · 1990'dan beri",
      services: "Hizmetler",
      company: "Kurumsal",
      legal: "Yasal",
      coverage: "Kapsam",
      whatsapp: "WhatsApp",
      email: "E-posta",
      phone: "Telefon",
      rights: "Tüm hakları saklıdır.",
      disclaimer: "Rota Yachting, Rota Denizcilik Turizm ve Dış Ticaret A.Ş. bünyesindeki yat acentesi birimidir.",
    },
    whatsapp: "WhatsApp'tan yazın",
  },

};

// ---------- Services (9) ----------
export const services = [
  {
    slug: "port-clearance-transit-log",
    icon: "FileCheck2",
    en: {
      name: "Port Clearance & Transit Log",
      short: "Full entry, transit and exit clearance under Turkish regulations — including the Transit Log for foreign-flagged yachts.",
      intro: "Port clearance is the paper backbone of every yacht call in Türkiye. Rota Yachting handles arrival and departure formalities end-to-end, including the Transit Log (Transitlog) required for foreign-flagged yachts cruising Turkish waters.",
      handles: [
        "Pre-arrival notifications to the Harbour Master",
        "Transit Log issuance and extensions",
        "Crew and guest arrival & departure formalities",
        "Vessel registration documents and verification",
        "Port dues, light dues and disbursement account",
        "Liaison with Harbour Master, Customs and Coast Guard",
      ],
      process: [
        "Receive ETA, yacht documents and crew list",
        "Submit pre-arrival declarations digitally",
        "On-arrival attend vessel with port authorities",
        "Issue Transit Log and clearance documents",
        "Provide final disbursement account on departure",
      ],
      why: "Errors in clearance paperwork cause detentions, fines and blacklisting. Our agency is licensed (License No. 0466) and submits documents on behalf of the vessel — so the captain never touches a counter.",
    },
    ru: {
      name: "Портовое оформление и Transit Log",
      short: "Полное оформление входа, транзита и выхода по турецким правилам — включая Transit Log для яхт под иностранным флагом.",
      intro: "Портовое оформление — документальная основа каждого захода яхты в Турцию. Rota Yachting берёт на себя формальности прихода и отхода под ключ, включая Transit Log, обязательный для яхт под иностранным флагом, идущих турецкими водами.",
      handles: [
        "Предварительные уведомления в капитанию порта",
        "Оформление и продление Transit Log",
        "Формальности прибытия и убытия экипажа и гостей",
        "Судовые документы и их верификация",
        "Портовые, маячные сборы и disbursement account",
        "Взаимодействие с капитанией, таможней и береговой охраной",
      ],
      process: [
        "Получаем ETA, документы яхты и список экипажа",
        "Подаём предварительные декларации в электронном виде",
        "По прибытии выходим на борт вместе с властями",
        "Выдаём Transit Log и документы оформления",
        "Передаём финальный disbursement account при отходе",
      ],
      why: "Ошибки в оформлении ведут к задержаниям, штрафам и чёрным спискам. Наше агентство лицензировано (лицензия № 0466) и подаёт документы от имени судна — капитану не нужно подходить к стойке.",
    },
  },
  {
    slug: "berth-reservations",
    icon: "Anchor",
    en: {
      name: "Berth Reservations & Port Coordination",
      short: "Priority berth allocation across Türkiye's premier marinas and commercial ports — including season-peak availability.",
      intro: "Whether you need a week in Yalıkavak in August or a quiet winter lay-up in Göcek, our relationships with marina operators across Türkiye secure berths that public channels often cannot.",
      handles: [
        "Advance berth reservations in high season",
        "Last-minute berth sourcing and swap coordination",
        "Marina contracts, rate negotiation and invoicing",
        "Shore power, water and mooring assistance",
        "Seasonal lay-up planning (winter / summer)",
        "Commercial port berthing for superyachts",
      ],
      process: [
        "Share LOA, beam, draft and preferred dates",
        "We circulate to our partner marina network",
        "Confirm availability and rates in writing",
        "Secure reservation with deposit or guarantee",
        "Coordinate arrival with dockmaster",
      ],
      why: "Peak season availability in Bodrum, Göcek and Marmaris is almost entirely pre-booked. Our agency relationships open doors that walk-up enquiries cannot.",
    },
    ru: {
      name: "Резервирование стоянок и координация в портах",
      short: "Приоритетное размещение в ведущих маринах и коммерческих портах Турции — включая пиковый сезон.",
      intro: "Нужна ли вам неделя в Ялыкавак в августе или тихий зимний лей-ап в Гёджеке — наши отношения с операторами марин по всей Турции обеспечивают места, недоступные по открытым каналам.",
      handles: [
        "Заблаговременные бронирования в высокий сезон",
        "Экстренный поиск стоянок и координация обмена",
        "Контракты марин, переговоры по ставкам, выставление счетов",
        "Береговое питание, вода, помощь со швартовкой",
        "Планирование сезонного лей-апа (зима / лето)",
        "Размещение суперъяхт в коммерческих портах",
      ],
      process: [
        "Вы сообщаете LOA, ширину, осадку и предпочтительные даты",
        "Мы рассылаем запрос по сети партнёрских марин",
        "Подтверждаем наличие и ставки письменно",
        "Закрепляем бронирование депозитом или гарантией",
        "Согласуем прибытие с dockmaster",
      ],
      why: "В пиковый сезон места в Бодруме, Гёджеке и Мармарисе забронированы заранее. Наши агентские связи открывают двери, которые запросам «с улицы» недоступны.",
    },
  },
  {
    slug: "customs-immigration",
    icon: "Stamp",
    en: {
      name: "Customs & Immigration",
      short: "Customs declaration, bonded stores, crew visas and immigration clearance — handled within the Turkish regulatory framework.",
      intro: "From temporary importation of the yacht to crew immigration, every customs and border formality is managed in-house by licensed Turkish customs brokers and immigration liaisons.",
      handles: [
        "Temporary importation under yacht regime",
        "Bonded stores sealing and release",
        "Crew visa guidance and residence permits",
        "Guest immigration at private arrivals",
        "Importation of spare parts and technical items",
        "VAT guidance and commercial yacht frameworks",
      ],
      process: [
        "Submit vessel and crew documents in advance",
        "Coordinate customs officers to the vessel",
        "Process bonded stores and sealing where required",
        "Handle guest and crew passport formalities",
        "Issue clearance on departure",
      ],
      why: "Turkish customs and immigration regulations are specific and actively enforced. A single overlooked form can halt movement for 48–72 hours. We prevent that.",
    },
    ru: {
      name: "Таможня и иммиграция",
      short: "Таможенные декларации, опломбирование запасов, визы экипажа и иммиграционное оформление — в рамках турецкого законодательства.",
      intro: "От временного ввоза яхты до иммиграции экипажа — все таможенные и пограничные формальности ведутся внутри агентства лицензированными брокерами.",
      handles: [
        "Временный ввоз в режиме яхты",
        "Опломбирование и выпуск бондовых запасов",
        "Визовое сопровождение экипажа и ВНЖ",
        "Иммиграция гостей при частных прибытиях",
        "Ввоз запчастей и технических позиций",
        "НДС и режимы коммерческих яхт",
      ],
      process: [
        "Заранее подаём документы судна и экипажа",
        "Координируем прибытие таможенных офицеров",
        "Оформляем бондовые запасы и пломбы при необходимости",
        "Проводим паспортные формальности гостей и экипажа",
        "Выдаём оформление при отходе",
      ],
      why: "Турецкие таможенные и иммиграционные правила специфичны и строго применяются. Одна пропущенная форма может остановить движение на 48–72 часа. Мы это предотвращаем.",
    },
  },
  {
    slug: "bunkering",
    icon: "Fuel",
    en: {
      name: "Bunkering Coordination",
      short: "Competitive duty-free and duty-paid bunker supply across Turkish ports, with quality sampling and delivery oversight.",
      intro: "Bunker prices in Türkiye vary meaningfully between ports, suppliers and regimes. We source, quote, nominate and supervise delivery — and protect the vessel with proper sampling and documentation.",
      handles: [
        "Duty-free bunker delivery under yacht regime",
        "Quotations from multiple Turkish suppliers",
        "Nomination, scheduling and attendance",
        "Barge and pipeline delivery coordination",
        "Sample taking and quality disputes",
        "Bunker Delivery Notes and settlement",
      ],
      process: [
        "Receive quantity, grade and required date",
        "Circulate to approved suppliers",
        "Issue comparative quotation to captain",
        "Nominate and schedule delivery",
        "Attend, witness samples, sign BDN",
      ],
      why: "A bad bunker can sideline a yacht for weeks. Proper nomination, attendance and sampling is the difference between a saving and a disaster.",
    },
    ru: {
      name: "Координация бункеровки",
      short: "Конкурентная поставка топлива (с пошлиной и без) в портах Турции с отбором проб и надзором за выдачей.",
      intro: "Цены на топливо в Турции заметно разнятся между портами, поставщиками и режимами. Мы находим, котируем, номинируем и контролируем выдачу — защищая судно правильным отбором проб и документами.",
      handles: [
        "Беспошлинная поставка в режиме яхты",
        "Котировки от нескольких турецких поставщиков",
        "Номинация, расписание и присутствие",
        "Координация бункеровки с баржи и трубопровода",
        "Отбор проб и разрешение споров по качеству",
        "Bunker Delivery Note и расчёты",
      ],
      process: [
        "Получаем количество, марку и дату",
        "Рассылаем одобренным поставщикам",
        "Передаём капитану сравнительную котировку",
        "Номинируем и ставим в расписание",
        "Присутствуем, заверяем пробы, подписываем BDN",
      ],
      why: "Плохое топливо выводит яхту из строя на недели. Правильная номинация, присутствие и отбор проб — разница между экономией и катастрофой.",
    },
  },
  {
    slug: "provisioning",
    icon: "UtensilsCrossed",
    heroVideo: "/assets/videos/provisioning.mp4",
    en: {
      name: "Provisioning & Supplies",
      short: "Galley provisioning, premium wines, flowers, linens and hotel services — sourced locally, delivered dockside.",
      intro: "From a single cheese the chef has been searching for to a full guest-week provisioning order, our provisioning desk delivers directly to the yacht with proper invoicing under the yacht regime.",
      handles: [
        "Galley and stewardess provisioning lists",
        "Premium wines, spirits and champagne",
        "Fresh flowers and table settings",
        "Guest gifts and concierge items",
        "Laundry, linen and hotel services",
        "Dockside delivery with cold-chain control",
      ],
      process: [
        "Receive provisioning list (Excel/PDF)",
        "Price the list within 24 hours",
        "Confirm order and delivery window",
        "Handle customs (bonded if applicable)",
        "Deliver dockside with invoice pack",
      ],
      why: "Yacht provisioning is not grocery shopping. It requires cold chain, presentation, premium sourcing and perfect timing — especially for charter turnarounds.",
    },
    ru: {
      name: "Снабжение и провиант",
      short: "Провиант для камбуза, премиальные вина, цветы, бельё и гостиничный сервис — локально, с доставкой к борту.",
      intro: "От одного сыра, который искал шеф, до полного гостевого недельного заказа — наш отдел снабжения доставляет напрямую к яхте с правильными счетами в режиме яхты.",
      handles: [
        "Листы провианта камбуза и стюардесс",
        "Премиальные вина, крепкие алкоголи и шампанское",
        "Свежие цветы и сервировка стола",
        "Гостевые подарки и консьерж-позиции",
        "Прачечная, бельё и гостиничные сервисы",
        "Доставка к борту с контролем холодовой цепи",
      ],
      process: [
        "Получаем список провианта (Excel/PDF)",
        "Котируем в течение 24 часов",
        "Подтверждаем заказ и окно доставки",
        "Обрабатываем таможню (бонд при необходимости)",
        "Доставляем к борту с комплектом счетов",
      ],
      why: "Яхтенное снабжение — не поход в магазин. Требуется холодовая цепь, презентация, премиальный источник и идеальный тайминг — особенно при чартерных оборотах.",
    },
  },
  {
    slug: "crew-guest-logistics",
    icon: "Users",
    en: {
      name: "Crew & Guest Logistics",
      short: "Private jet handling, helicopter transfers, VIP ground transport, crew changes and guest arrivals — coordinated end-to-end.",
      intro: "Owner and charter guest arrivals are moments where Rota Yachting earns its reputation. We manage the itinerary from private terminal to tender, invisibly.",
      handles: [
        "FBO and private jet handling liaison",
        "Helicopter landing coordination",
        "VIP chauffeur and luggage logistics",
        "Crew flight bookings and rotations",
        "Visa and entry formalities for guests",
        "Meet & greet at major Turkish airports",
      ],
      process: [
        "Receive flight manifest and preferences",
        "Coordinate FBO / terminal arrival",
        "Dispatch VIP ground transport",
        "Manage luggage and immigration",
        "Deliver guests dockside on time",
      ],
      why: "First impressions on charter are set at the airport, not the passerelle. Controlling this first mile is where discreet agency work matters most.",
    },
    ru: {
      name: "Логистика экипажа и гостей",
      short: "Обслуживание частных джетов, вертолётные трансферы, VIP-транспорт, ротации экипажа и встречи гостей — под ключ.",
      intro: "Прибытие владельца и чартерных гостей — момент, где Rota Yachting зарабатывает репутацию. Мы ведём программу от частного терминала до тендера — незаметно.",
      handles: [
        "Связь с FBO и обслуживание частных джетов",
        "Координация посадки вертолёта",
        "VIP-шофёр и логистика багажа",
        "Билеты и ротации экипажа",
        "Визы и формальности для гостей",
        "Meet & greet в крупных аэропортах Турции",
      ],
      process: [
        "Получаем манифест рейса и предпочтения",
        "Координируем прибытие в FBO / терминал",
        "Подаём VIP-транспорт",
        "Ведём багаж и иммиграцию",
        "Доставляем гостей к борту вовремя",
      ],
      why: "Первое впечатление на чартере создаётся в аэропорту, а не у трапа. Контроль первой мили — зона, где тихая агентская работа важнее всего.",
    },
  },
  {
    slug: "technical-support",
    icon: "Wrench",
    en: {
      name: "Technical Support & Spare Parts",
      short: "Emergency spare parts import, engineer call-outs, class and flag liaison, and shipyard coordination across Türkiye.",
      intro: "When a breakdown happens on charter, every hour counts. We import spare parts under expedited customs regimes, coordinate qualified engineers and manage shipyard periods.",
      handles: [
        "Expedited spare parts importation",
        "Engineer and technician call-outs",
        "Class society and flag state liaison",
        "Shipyard quotation and period oversight",
        "Diving, hull cleaning and underwater works",
        "Tender and toy service coordination",
      ],
      process: [
        "Receive urgent requisition and part numbers",
        "Source from manufacturer or approved channel",
        "Clear through customs under yacht regime",
        "Deliver part and engineer to vessel",
        "Document work for warranty / class",
      ],
      why: "A part stuck in customs costs more than the part itself. Our customs licence and supplier network routinely saves 48–72 hours on critical spares.",
    },
    ru: {
      name: "Техническая поддержка и запасные части",
      short: "Срочный ввоз запчастей, вызов инженеров, связь с классификационными обществами и координация верфей по Турции.",
      intro: "Когда поломка случается на чартере, каждый час на счету. Мы ввозим запчасти в ускоренных таможенных режимах, координируем инженеров и ведём верфные периоды.",
      handles: [
        "Ускоренный ввоз запасных частей",
        "Вызовы инженеров и техников",
        "Связь с классом и государством флага",
        "Котировки и надзор верфных работ",
        "Дайверы, очистка корпуса, подводные работы",
        "Сервис тендеров и водных игрушек",
      ],
      process: [
        "Получаем срочный запрос и партномера",
        "Находим у производителя или у одобренного канала",
        "Очищаем таможню в режиме яхты",
        "Доставляем запчасть и инженера к судну",
        "Документируем работу для гарантии / класса",
      ],
      why: "Запчасть, застрявшая на таможне, обходится дороже себя. Наша таможенная лицензия и сеть поставщиков регулярно экономят 48–72 часа на критических запчастях.",
    },
  },
  {
    slug: "concierge",
    icon: "Gem",
    en: {
      name: "Concierge & Local Assistance",
      short: "Restaurants, private chefs ashore, tour guides, cultural access, villa rentals and bespoke experiences along the Turkish coast.",
      intro: "Beyond the operational, we curate the ashore experience: tables at restaurants that appear fully booked, private access to archaeological sites, and experiences your guests remember.",
      handles: [
        "Restaurant reservations in high season",
        "Private guides and cultural access",
        "Hammam, spa and wellness bookings",
        "Villa and estate rentals ashore",
        "Private chefs and catering events",
        "Bespoke shore excursions",
      ],
      process: [
        "Understand guest profile and preferences",
        "Curate a shortlist of experiences",
        "Secure reservations and private access",
        "Brief crew and tenders on timings",
        "Quiet after-service follow-up",
      ],
      why: "Concierge is where yachting becomes memorable. Our local partners open doors — quietly, elegantly, without the guest ever noticing a booking was difficult.",
    },
    ru: {
      name: "Консьерж и локальная поддержка",
      short: "Рестораны, частные повара на берегу, гиды, культурный доступ, аренда вилл и уникальные впечатления на турецком побережье.",
      intro: "Помимо операционного — мы курируем береговой опыт: столики в якобы забронированных ресторанах, частный доступ к археологическим объектам, впечатления, которые гости запомнят.",
      handles: [
        "Бронирование ресторанов в высокий сезон",
        "Частные гиды и культурный доступ",
        "Хамам, спа и велнес",
        "Аренда вилл и поместий на берегу",
        "Частные повара и кейтеринг",
        "Индивидуальные береговые экскурсии",
      ],
      process: [
        "Понимаем профиль и предпочтения гостей",
        "Курируем короткий список впечатлений",
        "Закрепляем брони и частный доступ",
        "Инструктируем экипаж и тендеры по таймингам",
        "Тихое сопровождение после сервиса",
      ],
      why: "Консьерж — там, где яхтинг становится запоминающимся. Наши локальные партнёры открывают двери — тихо, элегантно, так, что гость не замечает сложности.",
    },
  },
  {
    slug: "cruising-support",
    icon: "Compass",
    en: {
      name: "Cruising Support in Türkiye",
      short: "Itinerary design, anchorage intelligence, weather routing and multi-port agency coverage for complete Turkish cruises.",
      intro: "Our cruising desk supports captains planning a week in the Gulf of Göcek or a full Turkish season from Istanbul to Antalya — with anchorage intelligence that only local presence can provide.",
      handles: [
        "Itinerary design and distance planning",
        "Anchorage and bay intelligence (depths, holding, crowds)",
        "Multi-port agency continuity",
        "Weather routing briefings",
        "SSR, blue card and marine park permits",
        "Charter grey-zone regulatory briefings",
      ],
      process: [
        "Discuss desired itinerary and dates",
        "Produce a draft cruising plan",
        "Pre-book berths and confirm anchorages",
        "Brief captain on regulatory points",
        "Provide single agency contact throughout",
      ],
      why: "A cruise planned on a paper chart looks easy. A cruise executed — with fuel, berths, provisioning and guests moving in sequence — requires a single agency coordinating every port.",
    },
    ru: {
      name: "Круизная поддержка в Турции",
      short: "Дизайн маршрутов, данные по якорным стоянкам, погодный роутинг и мультипортовое агентирование.",
      intro: "Наш круизный отдел поддерживает капитанов, планирующих неделю в заливе Гёджек или полный турецкий сезон от Стамбула до Анталии — с данными о якорных стоянках, которые даёт только местное присутствие.",
      handles: [
        "Проработка маршрута и расстояний",
        "Данные по бухтам и якорным стоянкам (глубины, грунт, загрузка)",
        "Непрерывность агентирования в нескольких портах",
        "Погодный роутинг",
        "SSR, синяя карта и разрешения морских парков",
        "Брифинги по серым зонам чартера",
      ],
      process: [
        "Обсуждаем желаемый маршрут и даты",
        "Готовим черновик круизного плана",
        "Бронируем стоянки и подтверждаем бухты",
        "Инструктируем капитана по регулированию",
        "Одна агентская точка контакта на весь маршрут",
      ],
      why: "Круиз на бумажной карте выглядит просто. Исполнение круиза — с топливом, стоянками, провиантом и гостями в одной последовательности — требует одного агентства, координирующего каждый порт.",
    },
  },
];

// ---------- Destinations (6) ----------
export const destinations = [
  {
    slug: "istanbul",
    heroVideo: "/assets/videos/istanbul-bosphorus-mosques.mp4",
    en: {
      name: "Istanbul",
      tagline: "Gateway between continents",
      intro: "Istanbul offers one of yachting's most dramatic arrival experiences — under the Bosphorus bridges, between two continents. Our team handles commercial and marina berthing, VIP arrivals and complex clearance for yachts transiting to or from the Black Sea.",
      ops: [
        "Bosphorus transit coordination",
        "Marina berths at Atakoy, Istanbul Marina, Tuzla",
        "Private jet handling (IST / SAW)",
        "Heritage tour and concierge access",
      ],
    },
    ru: {
      name: "Стамбул",
      tagline: "Ворота между континентами",
      intro: "Стамбул предлагает одно из самых драматичных прибытий в яхтинге — под мостами Босфора, между двумя континентами. Наша команда ведёт коммерческие и мариновые стоянки, VIP-прибытия и сложное оформление для яхт, транзитирующих в Чёрное море или из него.",
      ops: [
        "Координация транзита Босфора",
        "Стоянки в Atakoy, Istanbul Marina, Tuzla",
        "Обслуживание частных джетов (IST / SAW)",
        "Историко-культурные туры и консьерж",
      ],
    },
  },
  // ---------- Black Sea & Marmara extension (5) ----------
  {
    slug: "sinop",
    heroVideo: "/assets/videos/aerial-lighthouse-at-inceburun-sinop-turkey-inc-2025-12-17-20-23-42-utc_1_.mp4",
    images: [
      "/assets/photos/dji_fly_20230927_064938_342_1695786613786_photo_optimized.jpg",
      "/assets/photos/img_0385.jpg",
      "/assets/photos/dji_fly_20240529_151146_428_1716984715672_photo_optimized.jpg",
      "/assets/photos/dji_fly_20230927_064938_342_1695786613786_photo_optimized.jpg",
    ],
    en: {
      name: "Sinop",
      tagline: "Türkiye's northernmost natural harbour",
      intro: "Sinop is the safest natural deep-water harbour on the Black Sea coast and the home port of Rota Yachting since 1990. From here we coordinate every clearance, transit and crew exchange across Türkiye's Black Sea seaboard.",
      ops: [
        "Black Sea entry / exit clearance (24/7 coverage)",
        "Sinop Marina berth allocation",
        "Sinop Castle, Archaeological Museum and Diogenes monument tours",
        "Hamsilos Fjord and Boztepe lighthouse excursions",
        "Charter season provisioning from Sinop wholesale market",
      ],
      history: "Founded as Sinope (Σινώπη) by Milesian colonists in the 7th century BC, Sinop is one of the oldest continuously inhabited Greek-Anatolian ports. It was the birthplace of Diogenes the Cynic (c. 412 BC) and later the capital of Mithridates VI Eupator's Pontic Kingdom. The 1853 Battle of Sinop — the first naval engagement of the Crimean War — sealed the port's place in maritime history. Today the city sits on Boztepe Cape, the northernmost point of Türkiye, with twin natural harbours that have sheltered traders, navies and yachts for over 2,600 years.",
    },
    ru: {
      name: "Синоп",
      tagline: "Самая северная естественная гавань Турции",
      intro: "Синоп — самая безопасная глубоководная естественная гавань Чёрного моря и домашний порт Rota Yachting с 1990 года. Отсюда мы координируем оформления, транзиты и смены экипажа по всему турецкому черноморскому побережью.",
      ops: [
        "Оформление входа/выхода из Чёрного моря (24/7)",
        "Распределение мест в Sinop Marina",
        "Туры по крепости Синоп, Археологическому музею и памятнику Диогену",
        "Экскурсии к фьорду Хамсилос и маяку Бозтепе",
        "Снабжение из оптового рынка Синопа в чартерный сезон",
      ],
      history: "Основанный милетскими колонистами как Синопа (Σινώπη) в VII веке до н.э., Синоп — один из старейших непрерывно населённых греко-анатолийских портов. Здесь родился Диоген Синопский (около 412 г. до н.э.); позднее город стал столицей Понтийского царства Митридата VI Евпатора. Синопское сражение 1853 года — первый морской бой Крымской войны — закрепило его место в морской истории. Сегодня город расположен на мысе Бозтепе, самой северной точке Турции, с двойной естественной гаванью, укрывавшей торговцев, флоты и яхты более 2 600 лет.",
    },
    tr: {
      name: "Sinop",
      tagline: "Türkiye'nin en kuzeydeki doğal limanı",
      intro: "Sinop, Karadeniz kıyısının en güvenli derin doğal limanıdır ve 1990'dan beri Rota Yachting'in ana üssüdür. Buradan Türkiye'nin tüm Karadeniz hattındaki çıkış işlemlerini, transitleri ve mürettebat değişimlerini koordine ediyoruz.",
      ops: [
        "Karadeniz giriş/çıkış clearance (7/24)",
        "Sinop Marina yer tahsisi",
        "Sinop Kalesi, Arkeoloji Müzesi ve Diyojen Anıtı turları",
        "Hamsilos Fiyordu ve Boztepe Feneri gezileri",
        "Sinop toptancı hâlinden charter sezonu ikmali",
      ],
      history: "MÖ 7. yüzyılda Miletli kolonistler tarafından Sinope (Σινώπη) adıyla kurulan Sinop, kesintisiz iskân görmüş en eski Yunan-Anadolu limanlarından biridir. Sinoplu Diyojen'in (MÖ ~412) doğum yeridir; sonra Pontus Kralı VI. Mithridates Eupator'un başkenti olmuştur. 1853 Sinop Deniz Savaşı — Kırım Savaşı'nın ilk deniz çarpışması — şehrin denizcilik tarihindeki yerini perçinlemiştir. Bugün Türkiye'nin en kuzey noktası olan Boztepe Burnu üzerindeki şehir, ikiz doğal limanlarıyla 2 600 yılı aşkın süredir tüccarlara, donanmalara ve yatlara sığınak olmaktadır.",
    },
  },
  {
    slug: "samsun",
    heroVideo: "/assets/videos/aerial-samsun-city-center-and-samsun-port-4k-2025-12-17-20-33-25-utc.mp4",
    images: [
      "/assets/photos/photo-2025-03-25-13-57-43.jpg",
      "/assets/photos/img_0447.jpg",
      "/assets/photos/img_6816.jpg",
      "/assets/photos/photo-2025-03-25-13-57-43.jpg",
    ],
    en: {
      name: "Samsun",
      tagline: "The Black Sea's largest commercial port",
      intro: "Samsun anchors the central Black Sea coast — its commercial port, agricultural hinterland and modern airport make it a strategic transit and provisioning stop for yachts crossing between Sinop and the Caucasus.",
      ops: [
        "Samsun Port commercial and yacht berthing",
        "Bafra and Çarşamba delta provisioning",
        "Bandırma Vapuru / Atatürk Memorial heritage tours",
        "SZF Çarşamba Airport private jet handling",
        "Onward transit support to Trabzon and Georgia",
      ],
      history: "Settled as Amisos by Milesian Greeks in the 8th century BC, Samsun was a Pontic and later Roman trading colony. Its modern significance was sealed on 19 May 1919, when Mustafa Kemal Atatürk landed aboard the steamer Bandırma — the moment that opened the Turkish War of Independence. The Bandırma Vapuru replica and the city's monumental waterfront make Samsun a destination for both maritime professionals and history-conscious guests.",
    },
    ru: {
      name: "Самсун",
      tagline: "Крупнейший коммерческий порт Чёрного моря",
      intro: "Самсун — якорь центрального черноморского побережья. Коммерческий порт, сельскохозяйственный hinterland и современный аэропорт делают его стратегической точкой транзита и снабжения для яхт между Синопом и Кавказом.",
      ops: [
        "Коммерческие и яхтенные стоянки в порту Самсуна",
        "Снабжение через дельты Бафры и Чаршамбы",
        "Туры к пароходу «Бандырма» и мемориалу Ататюрка",
        "Обслуживание частных джетов в аэропорту Чаршамба (SZF)",
        "Поддержка перехода в Трабзон и Грузию",
      ],
      history: "Основанный милетцами как Амисос в VIII веке до н.э., Самсун был понтийской, затем римской торговой колонией. Его современное значение было закреплено 19 мая 1919 года, когда Мустафа Кемаль Ататюрк сошёл на берег с парохода «Бандырма» — момент, открывший Войну за независимость Турции. Реплика «Бандырмы» и величественная набережная делают Самсун маршрутом как для профессионалов моря, так и для гостей с историческим интересом.",
    },
    tr: {
      name: "Samsun",
      tagline: "Karadeniz'in en büyük ticari limanı",
      intro: "Samsun, orta Karadeniz kıyısının çapasıdır — ticari limanı, tarımsal hinterlandı ve modern havalimanı, Sinop ile Kafkasya arasındaki yatlar için stratejik bir transit ve ikmal noktası yapar.",
      ops: [
        "Samsun Limanı ticari ve yat yanaşması",
        "Bafra ve Çarşamba deltası ikmali",
        "Bandırma Vapuru / Atatürk Anıtı miras turları",
        "SZF Çarşamba Havalimanı özel jet hizmeti",
        "Trabzon ve Gürcistan'a transit desteği",
      ],
      history: "MÖ 8. yüzyılda Miletli Yunanlar tarafından Amisos adıyla kurulan Samsun, önce Pontus, sonra Roma'nın bir ticaret kolonisidir. Modern önemi 19 Mayıs 1919'da Mustafa Kemal Atatürk'ün Bandırma vapuruyla şehre çıkışıyla mühürlenmiştir — Türk Kurtuluş Savaşı'nın başlangıcı. Bandırma Vapuru replikası ve anıtsal sahil bandı, Samsun'u hem deniz profesyonelleri hem de tarih meraklısı konuklar için bir durak yapar.",
    },
  },
  {
    slug: "trabzon",
    heroVideo: "/assets/videos/turkey-trabzon-uzungol-2025-12-17-19-59-08-utc.mp4",
    images: [
      "/assets/photos/photo-2025-03-29-12-12-11.jpg",
      "/assets/photos/photo-2026-04-06-12-12-26.jpg",
      "/assets/photos/photo-2025-03-29-12-12-11.jpg",
      "/assets/photos/trabzon-quay-yachts.jpg",
    ],
    en: {
      name: "Trabzon",
      tagline: "Where the Silk Road met the sea",
      intro: "Trabzon — the eastern terminus of the Black Sea — combines a working commercial port with one of Anatolia's richest cultural hinterlands. We coordinate yacht entries, helicopter excursions to the Sumela Monastery and onward transit toward the Georgian coast.",
      ops: [
        "Trabzon Port clearance and berthing",
        "Sumela Monastery and Uzungöl helicopter tours",
        "Trabzon Airport private jet handling",
        "Eastern Black Sea tea / hazelnut artisanal provisioning",
        "Georgia (Batumi) onward transit coordination",
      ],
      history: "Founded as Trapezus (Τραπεζοῦς) by Milesian colonists in the 8th century BC, Trabzon became the seat of the Empire of Trebizond (1204–1461) — the last Greek state of the Byzantine world, ruled by the Komnenoi. Its monasteries (Sumela, perched 1,200 m on a cliff face), its Hagia Sophia and its caravan routes connecting Tabriz and Genoa made it the western terminus of the Silk Road. Twenty-six centuries later, the working port still serves vessels moving between the Caucasus and the Mediterranean.",
    },
    ru: {
      name: "Трабзон",
      tagline: "Где Шёлковый путь встречался с морем",
      intro: "Трабзон — восточный терминал Чёрного моря — сочетает действующий коммерческий порт с одним из богатейших культурных hinterland'ов Анатолии. Мы координируем заход яхт, вертолётные экскурсии в монастырь Сумела и дальнейший транзит к грузинскому побережью.",
      ops: [
        "Оформление и стоянки в порту Трабзона",
        "Вертолётные туры в Сумела и Узунгёль",
        "Обслуживание частных джетов в аэропорту Трабзона",
        "Восточно-черноморский чай и орех — ремесленное снабжение",
        "Координация транзита в Грузию (Батуми)",
      ],
      history: "Основанный милетцами как Трапезунд (Τραπεζοῦς) в VIII веке до н.э., Трабзон стал столицей Трапезундской империи (1204–1461) — последнего греческого государства византийского мира под властью Комнинов. Его монастыри (Сумела, на 1 200 м над пропастью), Айя-София и караванные пути, связывавшие Тебриз и Геную, сделали его западным терминалом Шёлкового пути. Двадцать шесть веков спустя действующий порт по-прежнему обслуживает суда между Кавказом и Средиземноморьем.",
    },
    tr: {
      name: "Trabzon",
      tagline: "İpek Yolu'nun denizle buluştuğu yer",
      intro: "Trabzon — Karadeniz'in doğu ucu — işleyen bir ticari limanı Anadolu'nun en zengin kültürel hinterlandlarından biriyle birleştirir. Yat girişlerini, Sümela Manastırı'na helikopter gezilerini ve Gürcistan kıyısına devam transit lojistiğini koordine ediyoruz.",
      ops: [
        "Trabzon Limanı clearance ve yanaşma",
        "Sümela Manastırı ve Uzungöl helikopter turları",
        "Trabzon Havalimanı özel jet hizmeti",
        "Doğu Karadeniz çay/fındık atölye ikmali",
        "Gürcistan (Batum) transit koordinasyonu",
      ],
      history: "MÖ 8. yüzyılda Miletli kolonistler tarafından Trapezus (Τραπεζοῦς) adıyla kurulan Trabzon, Komnenos hanedanının yönettiği Trabzon İmparatorluğu'na (1204–1461) başkentlik etti — Bizans dünyasının son Yunan devleti. 1 200 metrelik bir uçurum yamacındaki Sümela Manastırı, Ayasofya'sı ve Tebriz ile Cenova'yı bağlayan kervan yolları, şehri İpek Yolu'nun batı ucu yaptı. Yirmi altı yüzyıl sonra, faal liman hâlâ Kafkasya ile Akdeniz arasındaki gemilere hizmet veriyor.",
    },
  },
  {
    slug: "amasra",
    heroVideo: "/assets/videos/amasra-city-2025-12-17-20-34-58-utc.mp4",
    images: [
      "/assets/photos/aerial-view-of-amasra-district-of-bartin-province-2026-03-25-09-37-33-utc.jpg",
      "/assets/photos/7ecb6bc7-ba51-430b-8ed9-89e604bb8491_8321ba92-2371-47e5-942e-a0bd438a6f0a.jpg",
      "/assets/photos/img_1350.jpeg",
      "/assets/photos/amasra-sunseeker-cove.jpg",
    ],
    en: {
      name: "Amasra",
      tagline: "A Genoese fortress on twin coves",
      intro: "Amasra is one of the Black Sea's most photogenic anchorages — a small peninsula split into two coves and crowned by a fortress wall layered by Romans, Byzantines and Genoese. Quiet, picturesque and ideal for short-stop cruising guests.",
      ops: [
        "Amasra natural-cove anchorage support",
        "Bartın Province jetty and shore-side coordination",
        "Genoese Castle, Kuşkayası Yol Anıtı and historic-island walking tours",
        "Provisioning from Amasra weekend artisan market",
        "Onward transfer logistics to Ankara and Istanbul (4 h road)",
      ],
      history: "Originally the small Greek colony Sesamus, the city was renamed Amastris in the 4th century BC after the Persian princess Amastris, niece of Darius III, who united four cities under a single rule. Romans, Byzantines and finally the Genoese (14th century) each rebuilt the fortress walls that still ring the peninsula. UNESCO has placed Amasra's Genoese trade-route fortifications on its Tentative World Heritage List. The two natural coves — Boyuk and Küçük — have hosted Black Sea trading vessels for over 2,400 years.",
    },
    ru: {
      name: "Амасра",
      tagline: "Генуэзская крепость на двойных бухтах",
      intro: "Амасра — одна из самых фотогеничных якорных стоянок Чёрного моря: небольшой полуостров, разделённый на две бухты и увенчанный крепостной стеной, наложенной слоями римлянами, византийцами и генуэзцами. Тихая, живописная и идеальная для коротких заходов чартерных гостей.",
      ops: [
        "Поддержка на естественной якорной стоянке Амасры",
        "Координация причала и берега в провинции Бартын",
        "Прогулки по Генуэзской крепости, монументу Кушкаясы и историческому острову",
        "Снабжение с ремесленного weekend-рынка Амасры",
        "Логистика трансферов в Анкару и Стамбул (4 часа по трассе)",
      ],
      history: "Первоначально маленькая греческая колония Сезам, город был переименован в Амастрис в IV веке до н.э. в честь персидской принцессы Амастриды, племянницы Дария III, объединившей четыре города под одним правлением. Римляне, византийцы и, наконец, генуэзцы (XIV век) перестраивали крепостные стены, которые до сих пор окаймляют полуостров. ЮНЕСКО внёс генуэзские торговые укрепления Амасры в Предварительный список Всемирного наследия. Две естественные бухты — Боюк и Кючюк — принимают черноморские торговые суда более 2 400 лет.",
    },
    tr: {
      name: "Amasra",
      tagline: "İki koyun arasında bir Cenova kalesi",
      intro: "Amasra, Karadeniz'in en fotojenik demir noktalarından biridir — küçük bir yarımada iki koya bölünmüş, ve Romalıların, Bizans'ın ve Cenevizlilerin katmanladığı sur duvarlarıyla taçlandırılmıştır. Sessiz, pitoresk ve kısa duraklar için ideal.",
      ops: [
        "Amasra doğal koy demir desteği",
        "Bartın il iskele ve karasal koordinasyon",
        "Ceneviz Kalesi, Kuşkayası Yol Anıtı ve Tavşan Adası yürüyüş turları",
        "Amasra hafta sonu el sanatları pazarından ikmal",
        "Ankara ve İstanbul'a kara transfer lojistiği (4 saat)",
      ],
      history: "Önce küçük Yunan kolonisi Sesamos olan şehir, MÖ 4. yüzyılda III. Darius'un yeğeni Pers prensesi Amastris adına Amastris olarak yeniden adlandırıldı; prenses dört şehri tek yönetim altında birleştirdi. Romalılar, Bizans ve son olarak Cenevizliler (14. yüzyıl) yarımadayı çevreleyen sur duvarlarını yeniden inşa etti. UNESCO, Amasra'nın Ceneviz ticaret yolu surlarını Geçici Dünya Mirası Listesi'ne almıştır. Boyuk ve Küçük adlı iki doğal koy, 2 400 yılı aşkın süredir Karadeniz'in ticaret gemilerini ağırlamaktadır.",
    },
  },
  {
    slug: "canakkale",
    heroVideo: "/assets/videos/canakkale-martyrs-memorial-2025-12-17-14-13-10-utc.mp4",
    images: [
      "/assets/photos/canakkale-turkey-gallipoli-peninsula-photo-sho-2026-03-24-23-14-02-utc.jpg",
      "/assets/photos/photo-2023-11-20-22-58-21.jpg",
      "/assets/photos/photo-2023-07-23-21-39-18.jpg",
      "/assets/photos/canakkale-turkey-gallipoli-peninsula-photo-sho-2026-03-24-23-14-02-utc.jpg",
    ],
    en: {
      name: "Çanakkale",
      tagline: "The Dardanelles — three thousand years of strategic water",
      intro: "Çanakkale guards the Dardanelles strait — the only sea route between the Aegean and Marmara, and arguably the most historically charged stretch of water on earth. Every yacht moving between the Mediterranean circuit and Istanbul transits here. We handle pilotage, transit logging and shore-side culture.",
      ops: [
        "Dardanelles transit clearance and pilot coordination",
        "Troy Archaeological Park (UNESCO) private tours",
        "Gallipoli battlefield and ANZAC commemorative services",
        "Bozcaada and Gökçeada island transfer support",
        "North-Aegean to Marmara seasonal transit",
      ],
      history: "The Dardanelles — the ancient Hellespont — has framed history for three thousand years. On its southern shore stands Troy, the Bronze-Age city of Homer's Iliad, excavated by Schliemann in the 1870s and now a UNESCO World Heritage Site. In 1915 the Çanakkale Naval and Land Campaigns (the Gallipoli campaign for the Allies) became one of the defining battles of the First World War; here a then-unknown commander named Mustafa Kemal earned the rank that would later make him Atatürk. The strait remains, today as in antiquity, the gate between two seas.",
    },
    ru: {
      name: "Чанаккале",
      tagline: "Дарданеллы — три тысячи лет стратегической воды",
      intro: "Чанаккале охраняет Дарданелльский пролив — единственный морской путь между Эгейским и Мраморным морями и, возможно, самую исторически насыщенную полосу воды на земле. Каждая яхта, идущая между средиземноморским маршрутом и Стамбулом, проходит здесь. Мы обеспечиваем лоцманскую проводку, транзит-лог и культурную программу на берегу.",
      ops: [
        "Оформление транзита Дарданелл и координация лоцмана",
        "Частные туры в Археологический парк Трои (ЮНЕСКО)",
        "Поле сражений Галлиполи и ANZAC-памятные службы",
        "Поддержка трансферов на острова Бозджаада и Гёкчеада",
        "Сезонный транзит между Северной Эгейкой и Мрамором",
      ],
      history: "Дарданеллы — древний Геллеспонт — обрамляют историю уже три тысячи лет. На южном берегу стоит Троя, бронзовый город «Илиады» Гомера, раскопанный Шлиманом в 1870-х и сегодня входящий в Список Всемирного наследия ЮНЕСКО. В 1915 году Чанаккальские морские и сухопутные кампании (для союзников — Галлиполийская) стали одной из определяющих битв Первой мировой; здесь тогда неизвестный командующий Мустафа Кемаль заслужил звание, которое позже превратит его в Ататюрка. Пролив остаётся, как и в древности, воротами между двумя морями.",
    },
    tr: {
      name: "Çanakkale",
      tagline: "Çanakkale Boğazı — üç bin yıllık stratejik su",
      intro: "Çanakkale, Çanakkale Boğazı'nın bekçisidir — Ege ile Marmara arasındaki tek deniz yolu ve dünyanın belki de en yoğun tarihe sahip su şeridi. Akdeniz devresi ile İstanbul arasında hareket eden her yat buradan transit geçer. Pilot rehberliği, transit log ve karasal kültür programını biz koordine ederiz.",
      ops: [
        "Çanakkale Boğazı transit clearance ve pilot koordinasyonu",
        "Truva Antik Kenti (UNESCO) özel turları",
        "Gelibolu Tarihi Alan ve ANZAC anma hizmetleri",
        "Bozcaada ve Gökçeada transfer desteği",
        "Kuzey Ege – Marmara mevsimsel transit",
      ],
      history: "Çanakkale Boğazı — antik Hellespontos — üç bin yıldır tarihi çerçeveliyor. Güney kıyısında Troya yer alır: Homeros'un İlyada'sının Tunç Çağı şehri, 1870'lerde Schliemann tarafından kazılmış ve bugün UNESCO Dünya Mirası Listesi'nde. 1915'te Çanakkale Deniz ve Kara Muharebeleri (müttefikler için Gelibolu Harekâtı), Birinci Dünya Savaşı'nın belirleyici çarpışmalarından biri oldu; o tarihte henüz tanınmayan komutan Mustafa Kemal, ileride onu Atatürk yapacak rütbeyi burada kazandı. Boğaz, antik çağdaki gibi bugün de iki denizin kapısıdır.",
    },
  },
];

// ---------- Articles / News (5) ----------
export const articles = [
  {
    slug: "bosphorus-transit-2026-pilot-fees-booking",
    date: "2026-04-30",
    readTime: 11,
    image: "/assets/photos/insights-hero.jpg",
    category: { en: "Operations", ru: "Операции", tr: "Operasyon" },
    en: {
      title: "2026 Bosphorus Transit — pilot fees, booking windows and insider tips",
      excerpt: "Everything captains and yacht managers need to know before transiting the Bosphorus in 2026 — pilotage thresholds, current TÜRBAS fees, booking windows, weather windows, and what every yacht over 200 GT must arrange 48 hours in advance.",
      body: [
        { h: "Why the Bosphorus is unlike any other transit", p: "The Bosphorus is the only sea route between the Mediterranean and the Black Sea — 31 km of dense, two-way commercial traffic, narrow chokepoints (700 m at Anadoluhisarı), six course alterations exceeding 45° and tidal-style currents that can run 4–6 knots southbound in spring melt. Add 50,000+ commercial transits per year, scheduled ferry crossings every 6 minutes, and a Vessel Traffic Service (VTS) that holds the legal authority to suspend the strait at any moment for fog, current or military priority. This is why Türkiye's Maritime Code (Article 13) makes pilotage compulsory for almost every yacht above 200 GT and strongly recommends it for everyone else." },
        { h: "Who must take a pilot in 2026", p: "Compulsory: any yacht over 200 GT (gross tonnage), any yacht carrying dangerous cargo, any vessel with restricted manoeuvrability, and any captain making their first Bosphorus transit on that vessel. Strongly recommended (and operationally wise) for everything between 100–200 GT — single-handed sailings of yachts in this range against southbound spring currents have repeatedly ended in pilot intervention with significantly higher fees. Optional but advisable for vessels under 100 GT crossing in busy season (May–September). The pilot service is provided by the Turkish Maritime Pilots Association (TÜRBAS) under the General Directorate of Coastal Safety (Kıyı Emniyeti Genel Müdürlüğü)." },
        { h: "Current fee structure (2026 indicative)", p: "Pilot fees are GT-based and currently quoted in USD — the official tariff is updated annually by Kıyı Emniyeti. As an indicative range for 2026: yachts 200–500 GT pay roughly USD 1,400–2,800 for a single Bosphorus pilot transit; 500–1,000 GT roughly USD 2,800–5,200; 1,000–3,000 GT roughly USD 5,200–9,500; above 3,000 GT a custom quote applies. To this you add: VTS reporting fee, lighthouse contribution, mooring/unmooring if pilot board/disembark requires a service launch, and — outside daylight hours — a night surcharge of approximately 30%. Always request a written quotation from your agent before arrival; tariffs change each January and the published table is the only authoritative source." },
        { h: "The 48-hour booking window — non-negotiable", p: "Yacht agents must file the SP1 transit notification with İstanbul VTS at least 48 hours before transit. The notification includes: vessel particulars and last 5 ports, current GT/LOA/draft, intended direction (north or south), preferred ETA at the strait entry (Yenikapı for southbound, Türkeli for northbound), pilot and tug requirements, and dangerous-cargo declarations. Late submissions are accepted only at the Director's discretion and almost always trigger a re-slotting of 12–24 hours during peak season. For confidential or media-sensitive transits, agents can request a private timing window (early-morning is the most discreet)." },
        { h: "Weather and current windows that captains miss", p: "The Bosphorus runs predominantly south at the surface (1–3 knots typical, up to 6 in spring) and the opposite direction below ~30 m. Northbound transits in March–May fight a strong contrary current and are 30–50% slower than southbound. Fog: 15–25 days per year, mostly November–February, can suspend the strait for 6–18 hours. Lodos (south-westerly storm): 8–12 events per year, mostly autumn-winter, can close the strait or impose mandatory tug escort. The operational sweet spot for non-urgent transits: late June through September, daylight hours, northbound on a calm forenoon. We monitor METEU forecasts and VTS bulletins continuously and re-time transits proactively when sea conditions justify it." },
        { h: "What happens on the day", p: "T-04 hours: agent confirms VTS slot is still good and weather is clearing. T-02: pilot is dispatched from his station, agent boards the yacht at Tuzla or Pendik to co-ordinate. T-00: pilot embarks via service launch at the strait entry; bridge briefing covers route, planned course alterations, expected encounters with deep-draft commercial traffic, and language of communication (most TÜRBAS pilots speak fluent English). T+90 min (typical southbound): pilot disembarks at the southern strait exit; agent issues the Disbursement Account and copies of all official receipts within 24 hours. The transit itself is unforgettable — Sultanahmet from a yacht's bridge in early morning is one of yachting's iconic visual moments." },
        { h: "Common mistakes — and what they cost", p: "Underdeclaring GT to qualify out of compulsory pilotage: detected at boarding inspection, fine ~USD 5,000+ and forced re-pilotage. Booking the pilot privately (without an agent): possible for some local boats, but a foreign-flagged yacht must still file SP1 through an agent — bypassing leads to formal warning, then arrest of papers. Late ETA without notice: VTS may close your slot and re-schedule into the next 12-hour window — at the captain's commercial cost. Forgetting to declare dangerous cargo: this includes lithium-ion tender batteries above 100 Wh per cell — declarable in 2026, increasingly enforced. Skipping the agent for repeated transits: by the third transit the savings disappear behind one error." },
        { h: "How Rota Yachting handles your Bosphorus transit", p: "We file SP1 notifications and TÜRBAS pilot bookings within minutes of receiving your ETA. We embark senior agency staff at Tuzla, Pendik or Yenikapı to ride the transit alongside the pilot — for first-time captains this is the single biggest stress reducer. We hold direct relationships with Kıyı Emniyeti and the major İstanbul marinas (Yalıkavak's İstanbul partners, Atakoy, Çelebi, Setur), so post-transit berthing can be arranged within the same notification. And we work in three time zones — every Bosphorus inquiry is answered within minutes on WhatsApp +90 532 472 92 68 in English, Russian and Turkish." },
        { h: "When to call us", p: "Send your ETA and tonnage 7–10 days before your intended transit window if possible — earlier is always better in May–September. For owners considering a discreet transit (early morning, no shore vessels), we recommend 14 days' notice so we can secure a quiet slot. For Black Sea exits (Sinop → Bosphorus southbound), we co-ordinate the Sinop Transit Log clearance and the Bosphorus pilot booking under a single agency contract. Beyond the ordinary — quietly executed." },
      ],
    },
    ru: {
      title: "Транзит Босфора 2026 — лоцманские сборы, окна бронирования и инсайдерские советы",
      excerpt: "Всё, что нужно знать капитанам и яхтенным менеджерам перед транзитом Босфора в 2026 году — пороги обязательной лоцманской проводки, текущие тарифы TÜRBAS, окна бронирования, погодные окна и что каждая яхта свыше 200 GT обязана организовать за 48 часов.",
      body: [
        { h: "Почему Босфор — это особый транзит", p: "Босфор — единственный морской путь между Средиземным и Чёрным морями: 31 км плотного двустороннего коммерческого трафика, узкие места (700 м у Анадолухисары), шесть изменений курса свыше 45° и течения, в весеннее половодье разгоняющиеся до 4–6 узлов на юг. Добавьте 50 000+ коммерческих транзитов в год, паромы каждые 6 минут и Службу управления движением судов (VTS), имеющую право в любой момент закрыть пролив из-за тумана, течения или военных приоритетов. Именно поэтому Морской кодекс Турции (статья 13) делает лоцманскую проводку обязательной практически для всех яхт свыше 200 GT и настоятельно рекомендует её для остальных." },
        { h: "Кто обязан брать лоцмана в 2026", p: "Обязательно: любая яхта свыше 200 GT, любая яхта с опасным грузом, любое судно с ограниченной манёвренностью и капитан, проходящий Босфор на этом судне впервые. Настоятельно рекомендуется для 100–200 GT — одиночные проходы против весенних южных течений неоднократно заканчивались экстренным вызовом лоцмана со значительным удорожанием. Опционально, но желательно для судов до 100 GT в высокий сезон (май–сентябрь). Лоцманскую службу обеспечивает Турецкая ассоциация морских лоцманов (TÜRBAS) под управлением Главного управления береговой безопасности (Kıyı Emniyeti)." },
        { h: "Текущая структура сборов (2026, ориентировочно)", p: "Лоцманские сборы рассчитываются по GT и сейчас номинированы в USD — официальный тариф обновляется Kıyı Emniyeti ежегодно. Ориентировочно на 2026: 200–500 GT — около USD 1 400–2 800 за один проход; 500–1 000 GT — около USD 2 800–5 200; 1 000–3 000 GT — около USD 5 200–9 500; свыше 3 000 GT — индивидуальная котировка. К этому добавляются: сбор VTS, маячный сбор, услуги катера для посадки/высадки лоцмана, а вне светового дня — ночная надбавка ~30%. Всегда запрашивайте у агента письменную котировку до прибытия; тарифы меняются каждый январь, и опубликованная таблица — единственный авторитетный источник." },
        { h: "48-часовое окно бронирования — без обсуждения", p: "Агент обязан подать уведомление SP1 в VTS Стамбула не менее чем за 48 часов до транзита. Уведомление включает: данные судна и последние 5 заходов, актуальный GT/LOA/осадку, направление (север или юг), желаемое ETA на входе (Yenikapı для южного, Türkeli для северного), требования к лоцману и буксиру, декларации по опасному грузу. Поздняя подача принимается только по решению начальника и почти всегда приводит к пересдвижке слота на 12–24 часа в высокий сезон. Для конфиденциальных или медиа-чувствительных транзитов агент может запросить частное окно (раннее утро — самое незаметное)." },
        { h: "Погодные и течевые окна, которые упускают капитаны", p: "Босфор в основном течёт на юг на поверхности (1–3 узла, до 6 весной) и в обратную сторону ниже ~30 м. Северные транзиты в марте–мае на 30–50% медленнее. Туманы: 15–25 дней в году, в основном ноябрь–февраль, могут закрыть пролив на 6–18 часов. Лодос (юго-западный шторм): 8–12 случаев в году, осенью-зимой, может закрыть пролив или ввести обязательный буксир. Операционное «окно мечты» для несрочных транзитов: с конца июня по сентябрь, светлое время, северный курс в спокойное утро. Мы непрерывно отслеживаем METEU и VTS-бюллетени и проактивно переносим транзиты, когда море того требует." },
        { h: "Что происходит в день транзита", p: "T-04 часа: агент подтверждает слот VTS и улучшение погоды. T-02: лоцман выходит со станции, агент поднимается на борт у Тузлы или Пендика для координации. T-00: лоцман высаживается с катера у входа в пролив; на мостике брифинг по маршруту, изменениям курса, ожидаемому встречному коммерческому трафику и языку связи (большинство лоцманов TÜRBAS — свободный английский). T+90 мин (типично на юг): лоцман сходит у южного выхода; агент в течение 24 часов выдаёт Disbursement Account с копиями всех квитанций. Сам транзит незабываем — Султанахмет с мостика яхты на рассвете — один из иконических визуальных моментов яхтинга." },
        { h: "Частые ошибки — и что они стоят", p: "Занижение GT для обхода обязательной проводки: вскрывается при инспекции, штраф USD 5 000+ и принудительная проводка. Прямое бронирование лоцмана без агента: для некоторых местных судов возможно, но иностранно-флаговая яхта обязана подавать SP1 через агента — обход ведёт к формальному предупреждению и аресту документов. Опоздание ETA без уведомления: VTS закрывает слот и переносит на следующее 12-часовое окно за коммерческий счёт капитана. Забытая декларация опасного груза: включая литий-ионные батареи тендеров выше 100 Вт·ч на ячейку — декларируется в 2026 и всё строже контролируется. Отказ от агента при повторных транзитах: к третьему проходу экономия исчезает за одной ошибкой." },
        { h: "Как Rota Yachting проводит ваш транзит Босфора", p: "Мы подаём уведомления SP1 и резервируем лоцмана TÜRBAS в минутах от получения ETA. Старший представитель агентства поднимается на борт в Тузле, Пендике или Yenikapı и сопровождает транзит вместе с лоцманом — для капитана-новичка на этом маршруте это самое большое снижение стресса. У нас прямые отношения с Kıyı Emniyeti и крупнейшими маринами Стамбула (Yalıkavak İstanbul, Atakoy, Çelebi, Setur), поэтому стоянка после транзита оформляется в том же запросе. Мы работаем в трёх часовых поясах — каждый запрос по Босфору отвечается на WhatsApp +90 532 472 92 68 в английском, русском и турецком за минуты." },
        { h: "Когда нам звонить", p: "Пришлите ETA и тоннаж за 7–10 дней до планируемого транзитного окна — раньше всегда лучше в мае–сентябре. Для деликатных транзитов (раннее утро, без береговых судов) рекомендуем 14 дней. Для выхода из Чёрного моря (Синоп → Босфор на юг) мы координируем оформление Transit Log в Синопе и резервирование лоцмана Босфора по единому агентскому контракту. Beyond the Ordinary — тихо исполнено." },
      ],
    },
    tr: {
      title: "2026 Boğaz Transiti — pilot ücretleri, rezervasyon pencereleri ve içerden ipuçları",
      excerpt: "2026'da İstanbul Boğazı'ndan transit geçmeden önce kaptan ve yat yöneticilerinin bilmesi gereken her şey — zorunlu kılavuz kaptan eşikleri, güncel TÜRBAS ücretleri, rezervasyon pencereleri, hava pencereleri ve 200 GT üstü her yatın 48 saat önceden ayarlamak zorunda olduğu konular.",
      body: [
        { h: "Boğaz neden başka hiçbir transit'e benzemez", p: "İstanbul Boğazı, Akdeniz ile Karadeniz arasındaki tek deniz yolu — 31 km yoğun çift yönlü ticari trafik, dar geçiş noktaları (Anadoluhisarı'nda 700 m), 45°'yi aşan altı rota değişimi ve bahar erimesinde güney yönünde 4–6 knot'a varan akıntılar. Buna ekle: yıllık 50.000+ ticari transit, her 6 dakikada bir vapur seferi ve sis, akıntı veya askeri öncelik nedeniyle herhangi bir an boğazı askıya alma yetkisine sahip Vessel Traffic Service (VTS). İşte bu yüzden Türkiye Denizcilik Kanunu'nun 13. maddesi, 200 GT üzerindeki neredeyse her yat için kılavuz kaptan zorunluluğu getirir; geri kalan herkes için de güçlü tavsiye olarak durur." },
        { h: "2026'da kim kılavuz kaptan almak zorunda", p: "Zorunlu: 200 GT üzerindeki her yat, tehlikeli yük taşıyan her yat, manevra kabiliyeti kısıtlı her tekne ve o tekneyle ilk Boğaz geçişini yapan her kaptan. Güçlü tavsiye (ve operasyonel akıl) 100–200 GT arasındakiler için — bahar güney akıntılarına karşı tek başına geçişler defalarca acil pilot müdahalesiyle ve önemli ek ücretle sonuçlandı. 100 GT altı yatlar için yoğun sezonda (Mayıs–Eylül) opsiyonel ama tavsiye edilir. Kılavuzluk hizmetini Kıyı Emniyeti Genel Müdürlüğü'ne bağlı Türk Denizcilik Pilotları Birliği (TÜRBAS) sağlar." },
        { h: "Güncel ücret yapısı (2026 — gösterge niteliğinde)", p: "Pilot ücretleri GT bazlıdır ve şu an USD üzerinden kote edilir; resmi tarife Kıyı Emniyeti tarafından her yıl güncellenir. 2026 için gösterge aralık: 200–500 GT için tek geçiş yaklaşık USD 1.400–2.800; 500–1.000 GT yaklaşık USD 2.800–5.200; 1.000–3.000 GT yaklaşık USD 5.200–9.500; 3.000 GT üstü için özel teklif. Buna eklenir: VTS bildirim ücreti, fener katkısı, pilot binme/inme için romorkör/servis botu hizmeti, gün ışığı dışında ~%30 gece zammı. Varıştan önce her zaman acentenizden yazılı teklif isteyin; tarifeler her Ocak değişir ve yayımlanan tablo tek otorite kaynaktır." },
        { h: "48 saatlik rezervasyon penceresi — pazarlığa açık değil", p: "Acente, transit'ten en az 48 saat önce SP1 bildirimini İstanbul VTS'ye sunmak zorunda. Bildirim şunları içerir: gemi bilgileri ve son 5 liman, güncel GT/LOA/draft, yön (kuzey ya da güney), boğaz girişinde tercih edilen ETA (güney yön için Yenikapı, kuzey yön için Türkeli), pilot ve romorkör gereksinimleri, tehlikeli yük beyanları. Geç sunumlar yalnızca Müdür takdiriyle kabul edilir ve yoğun sezonda neredeyse her zaman 12–24 saatlik bir slot kaymasına neden olur. Gizli veya medya-hassas transitler için acente özel bir zaman penceresi talep edebilir (sabahın erken saatleri en sessizidir)." },
        { h: "Kaptanların kaçırdığı hava ve akıntı pencereleri", p: "Boğaz yüzeyde ağırlıkla güneye akar (tipik 1–3 knot, baharda 6'ya kadar) ve ~30 m altında ters yöne. Mart–Mayıs kuzey transitleri ters akıntıyla mücadele eder ve %30–50 daha yavaştır. Sis: yılda 15–25 gün, ağırlıkla Kasım–Şubat, boğazı 6–18 saat askıya alabilir. Lodos: yılda 8–12 olay, ağırlıkla sonbahar-kış, boğazı kapatabilir veya zorunlu romorkör eskortu getirebilir. Acil olmayan transitler için operasyonel altın pencere: Haziran sonundan Eylül'e, gün ışığı, sakin bir öğleden önce kuzey yönü. METEU tahminlerini ve VTS bültenlerini sürekli izler, deniz koşulları gerektirdiğinde transitleri proaktif olarak yeniden zamanlarız." },
        { h: "Transit gününde olanlar", p: "T-04 saat: acente VTS slotunun hâlâ geçerli ve havanın açıldığını teyit eder. T-02: pilot istasyonundan yola çıkar, acente Tuzla veya Pendik'te yata çıkıp koordinasyona başlar. T-00: pilot servis botuyla boğaz girişinde gemiye biner; köprü brifingi rota, planlanan rota değişimleri, beklenen ticari trafikle karşılaşmalar ve iletişim dilini kapsar (TÜRBAS pilotlarının çoğu akıcı İngilizce konuşur). T+90 dk (tipik güney): pilot güney çıkışta iner; acente 24 saat içinde Disbursement Account ile tüm makbuzların kopyalarını teslim eder. Transit'in kendisi unutulmaz — sabahın erken saatlerinde yat köprüsünden Sultanahmet, yatçılığın ikonik görsel anlarından biridir." },
        { h: "Sık yapılan hatalar — ve maliyetleri", p: "Zorunlu kılavuzluğu atlamak için GT'yi düşük beyan etmek: biniş muayenesinde tespit edilir, ~USD 5.000+ ceza ve zorunlu yeniden pilotaj. Acentesiz pilot rezervasyonu: bazı yerel tekneler için mümkün, ancak yabancı bayraklı bir yat SP1'i acente üzerinden vermek zorundadır — atlama, resmi uyarı ve evrak haczine yol açar. Bildirimsiz geç ETA: VTS slotunuzu kapatır ve sonraki 12 saatlik pencereye yeniden planlar — kaptana ticari maliyetiyle. Tehlikeli yük beyanını unutmak: hücre başına 100 Wh üzeri lityum-iyon tender bataryaları dahil — 2026'da beyan zorunlu, denetim sıkılaşıyor. Tekrarlayan transitlerde acenteyi atlamak: üçüncü geçişe kadar tasarruf, tek bir hatayla erir." },
        { h: "Rota Yachting Boğaz transitinizi nasıl yürütür", p: "ETA'nızı aldığımız dakikalar içinde SP1 bildirimini ve TÜRBAS pilot rezervasyonunu yaparız. Tuzla, Pendik veya Yenikapı'da kıdemli acente personelimiz yata çıkar ve transit boyunca pilotun yanında yer alır — ilk kez geçen kaptanlar için en büyük stres azaltıcıdır. Kıyı Emniyeti ve İstanbul'un büyük marinalarıyla (Yalıkavak İstanbul ortakları, Atakoy, Çelebi, Setur) doğrudan ilişkilerimiz vardır; transit sonrası yer aynı bildirimle ayarlanır. Üç saat dilimde çalışırız — her Boğaz sorgusu WhatsApp +90 532 472 92 68 üzerinden İngilizce, Rusça ve Türkçe olarak dakikalar içinde yanıtlanır." },
        { h: "Bizi ne zaman arayın", p: "Mümkünse planladığınız transit penceresinden 7–10 gün önce ETA ve tonajınızı gönderin — Mayıs–Eylül'de erken her zaman daha iyidir. Diskret transit düşünen armatörler için (sabahın erken saatleri, kıyı teknesi yok) sessiz bir slot ayırabilmemiz için 14 gün önceden bildirim öneriyoruz. Karadeniz çıkışları (Sinop → güneye Boğaz) için Sinop Transit Log çıkışını ve Boğaz pilot rezervasyonunu tek bir acentelik sözleşmesi altında koordine ederiz. Beyond the Ordinary — sessizce icra edilir." },
      ],
    },
  },
  {
    slug: "evolution-yacht-agency-services-turkey",
    date: "2026-04-29",
    readTime: 8,
    image: "/assets/photos/stock-luxury-marina-sunset.jpg",
    category: { en: "Industry", ru: "Индустрия", tr: "Sektör" },
    en: {
      title: "The Evolution of Yacht Agency Services in Türkiye",
      excerpt: "From basic port assistance to integrated, full-service operations — how Türkiye's yacht agency sector matured into a strategic component of the global yachting network, with Rota Maritime helping define the new standard.",
      body: [
        { h: "Powered by Rota Maritime expertise", p: "Over the past two decades, Türkiye has emerged as one of the most strategic and rapidly developing hubs in the global yachting industry. Positioned at the crossroads of Europe, Asia and the Mediterranean basin, the country offers a unique combination of geographical advantage, cultural richness and expanding maritime infrastructure. At the centre of this transformation stands the evolution of yacht agency services — a sector that has transitioned from basic port assistance to a highly specialised, full-service operational discipline. Companies like Rota Maritime and its yachting division, Rota Yachting Agency, play a pivotal role in shaping this new standard." },
        { h: "From traditional port services to integrated yacht agency solutions", p: "Historically, yacht support in Türkiye was limited to fundamental services such as berth arrangements, customs clearance and provisioning. With the increasing presence of superyachts and international clientele, expectations have significantly evolved. Today, a modern yacht agency in Türkiye must deliver: seamless port and marina coordination; advanced customs and immigration handling; technical support and shipyard liaison; luxury provisioning and concierge services; regulatory compliance and documentation management. Rota Maritime, leveraging its extensive background in commercial shipping through Rota Shipping, has successfully transferred its operational precision into the yachting sector — setting a benchmark for reliability and efficiency." },
        { h: "Türkiye as a strategic yachting destination", p: "The Turkish coastline, stretching across the Aegean and Mediterranean, is now recognised among the world's premier cruising grounds. Destinations such as Bodrum, Marmaris, Göcek and Istanbul have become essential stops for international yacht itineraries. With world-class marinas, competitive operational costs and a highly skilled maritime workforce, Türkiye continues to attract both private and charter yachts on a global scale." },
        { h: "The role of Rota Yachting Agency in modern maritime operations", p: "As a specialised division of Rota Maritime, Rota Yachting Agency represents a new generation of yacht agency services — combining traditional maritime discipline with bespoke yachting solutions. Key differentiators: Operational Excellence — backed by decades of commercial maritime experience. Local Expertise — deep knowledge of Turkish ports, marinas and regulations. Global Standards — service quality aligned with international superyacht expectations. 24/7 Support — continuous assistance for captains, crew and owners. Unlike conventional brokerage-focused firms, Rota Yachting Agency operates strictly as a maritime service provider, ensuring neutrality, efficiency and operational clarity." },
        { h: "Digitalisation and the future of yacht agency in Türkiye", p: "The next phase of development in Türkiye's yachting sector is driven by digital transformation. From real-time port coordination systems to AI-supported logistics and client communication platforms, yacht agencies are becoming more agile and data-driven. Rota Maritime is actively aligning with this evolution by integrating: smart port coordination tools; digital documentation workflows; real-time operational tracking; enhanced client communication systems. This forward-thinking approach ensures that Rota Yachting Agency remains at the forefront of innovation in Türkiye's maritime ecosystem." },
        { h: "Beyond the Ordinary", p: "Türkiye's yacht agency sector is no longer an emerging market — it is a mature, competitive and strategically vital component of the global yachting network. With its strong maritime heritage and commitment to excellence, Rota Yachting Agency, under the umbrella of Rota Maritime, continues to redefine industry standards. Beyond the Ordinary is not just a slogan — it reflects a service philosophy built on precision, trust and continuous evolution." },
      ],
    },
    ru: {
      title: "Эволюция услуг яхтенного агентирования в Турции",
      excerpt: "От базовой портовой поддержки к интегрированному полному циклу — как турецкий сектор яхтенного агентирования превратился в стратегический компонент мировой яхтенной сети, и какую роль в формировании нового стандарта играет Rota Maritime.",
      body: [
        { h: "Опираясь на экспертизу Rota Maritime", p: "За последние два десятилетия Турция стала одним из самых стратегических и быстро развивающихся центров мировой яхтенной индустрии. Расположенная на перекрёстке Европы, Азии и Средиземноморского бассейна, страна предлагает уникальное сочетание географических преимуществ, культурного богатства и расширяющейся морской инфраструктуры. В центре этой трансформации — эволюция услуг яхтенного агентирования: сектор перешёл от базовой поддержки в порту к высокоспециализированной операционной дисциплине полного цикла. Такие компании, как Rota Maritime, и её яхтенное подразделение Rota Yachting Agency играют ключевую роль в формировании этого нового стандарта." },
        { h: "От традиционных портовых услуг к интегрированным агентским решениям", p: "Исторически поддержка яхт в Турции ограничивалась стоянками, таможенным оформлением и снабжением. С ростом числа суперъяхт и международных клиентов ожидания существенно изменились. Сегодня современное яхтенное агентство в Турции должно обеспечивать: бесшовную координацию портов и марин; продвинутое таможенное и иммиграционное оформление; техническую поддержку и работу со верфями; премиальное снабжение и консьерж-сервис; соблюдение регуляторных требований и документооборот. Rota Maritime, опираясь на большой опыт коммерческого судоходства через Rota Shipping, успешно перенесла свою операционную точность в яхтенный сектор — задавая планку надёжности и эффективности." },
        { h: "Турция как стратегическое яхтенное направление", p: "Турецкое побережье, простирающееся вдоль Эгейского и Средиземного морей, сегодня входит в число ведущих круизных регионов мира. Такие направления, как Бодрум, Мармарис, Гёджек и Стамбул, стали обязательными остановками международных яхтенных маршрутов. Благодаря маринам мирового уровня, конкурентоспособной операционной стоимости и квалифицированному морскому персоналу Турция продолжает привлекать как частные, так и чартерные яхты в глобальном масштабе." },
        { h: "Роль Rota Yachting Agency в современных морских операциях", p: "Будучи специализированным подразделением Rota Maritime, Rota Yachting Agency представляет новое поколение яхтенного агентирования — сочетание традиционной морской дисциплины и персональных яхтенных решений. Ключевые отличия: Операционное превосходство — десятилетия коммерческого морского опыта. Локальная экспертиза — глубокое знание турецких портов, марин и нормативной базы. Глобальные стандарты — качество услуг, соответствующее ожиданиям международного суперъяхтенного рынка. Поддержка 24/7 — непрерывная помощь капитанам, экипажам и владельцам. В отличие от компаний, ориентированных на брокеридж, Rota Yachting Agency работает строго как поставщик морских услуг — обеспечивая нейтральность, эффективность и операционную ясность." },
        { h: "Цифровизация и будущее яхтенного агентирования в Турции", p: "Следующий этап развития сектора движется цифровой трансформацией. От систем координации порта в реальном времени до логистических платформ с поддержкой ИИ — агентства становятся гибче и опираются на данные. Rota Maritime активно встраивается в эту эволюцию: умные инструменты координации порта; цифровой документооборот; отслеживание операций в реальном времени; продвинутые системы коммуникации с клиентом. Такой подход обеспечивает Rota Yachting Agency ведущую позицию в инновациях турецкой морской экосистемы." },
        { h: "Beyond the Ordinary", p: "Сектор яхтенного агентирования Турции больше не «развивающийся рынок» — это зрелая, конкурентоспособная и стратегически важная часть мировой яхтенной сети. Опираясь на сильное морское наследие и приверженность совершенству, Rota Yachting Agency под зонтом Rota Maritime продолжает переопределять отраслевые стандарты. Beyond the Ordinary — не просто слоган: это сервисная философия, построенная на точности, доверии и непрерывном развитии." },
      ],
    },
    tr: {
      title: "Türkiye'de Yat Acentelik Hizmetlerinin Evrimi",
      excerpt: "Temel liman desteğinden entegre ve tam kapsamlı operasyonlara — Türkiye'nin yat acenteliği sektörü küresel yatçılık ağının stratejik bir bileşenine nasıl olgunlaştı ve Rota Maritime bu yeni standardı nasıl tanımlıyor.",
      body: [
        { h: "Rota Maritime uzmanlığıyla", p: "Son yirmi yılda Türkiye, küresel yatçılık endüstrisinin en stratejik ve hızla gelişen merkezlerinden biri haline geldi. Avrupa, Asya ve Akdeniz havzasının kavşağında konumlanan ülke, coğrafi avantaj, kültürel zenginlik ve büyüyen denizcilik altyapısının nadir bir bileşimini sunuyor. Bu dönüşümün merkezinde yat acentelik hizmetlerinin evrimi yer alıyor — temel liman desteğinden son derece uzmanlaşmış, tam kapsamlı bir operasyonel disipline geçen bir sektör. Rota Maritime ve onun yatçılık kolu Rota Yachting Agency, bu yeni standardın şekillenmesinde belirleyici bir rol oynuyor." },
        { h: "Geleneksel liman hizmetlerinden entegre yat acentelik çözümlerine", p: "Tarihsel olarak Türkiye'de yat desteği yer ayırma, gümrük çıkış işlemleri ve ikmal gibi temel hizmetlerle sınırlıydı. Süperyatların ve uluslararası müşterilerin artan varlığıyla beklentiler önemli ölçüde gelişti. Bugün modern bir yat acentesi Türkiye'de şunları sağlamak zorunda: kusursuz liman ve marina koordinasyonu; ileri düzey gümrük ve göçmenlik işlemleri; teknik destek ve tersane irtibatı; lüks ikmal ve konsiyerj hizmetleri; mevzuata uyum ve belge yönetimi. Rota Maritime, Rota Shipping aracılığıyla edindiği geniş ticari denizcilik birikimini yatçılık sektörüne başarıyla aktarmış; güvenilirlik ve verimlilik için bir referans noktası belirlemiştir." },
        { h: "Stratejik bir yatçılık destinasyonu olarak Türkiye", p: "Ege ve Akdeniz boyunca uzanan Türk kıyıları, bugün dünyanın önde gelen seyir bölgeleri arasında sayılıyor. Bodrum, Marmaris, Göcek ve İstanbul gibi destinasyonlar uluslararası yat rotalarının vazgeçilmez durakları haline geldi. Dünya standardındaki marinaları, rekabetçi operasyonel maliyetleri ve nitelikli denizcilik iş gücüyle Türkiye, hem özel hem charter yatları küresel ölçekte çekmeye devam ediyor." },
        { h: "Modern denizcilik operasyonlarında Rota Yachting Agency'nin rolü", p: "Rota Maritime'ın uzmanlaşmış bir bölümü olan Rota Yachting Agency, geleneksel denizcilik disiplinini özel yatçılık çözümleriyle birleştirerek yeni nesil yat acentelik hizmetlerini temsil ediyor. Temel farklılaştırıcılar: Operasyonel Mükemmellik — onlarca yıllık ticari denizcilik deneyimi. Yerel Uzmanlık — Türk limanları, marinalar ve mevzuat hakkında derin bilgi. Küresel Standartlar — uluslararası süperyat beklentileriyle uyumlu hizmet kalitesi. 7/24 Destek — kaptanlar, mürettebat ve armatörler için sürekli destek. Brokerage odaklı geleneksel firmaların aksine Rota Yachting Agency, sadece bir denizcilik hizmet sağlayıcı olarak çalışır; tarafsızlık, verimlilik ve operasyonel netlik garanti eder." },
        { h: "Dijitalleşme ve Türkiye'de yat acenteliğinin geleceği", p: "Sektördeki bir sonraki evre, dijital dönüşüm tarafından yönlendiriliyor. Gerçek zamanlı liman koordinasyon sistemlerinden yapay zeka destekli lojistik ve müşteri iletişim platformlarına kadar yat acenteleri daha çevik ve veri odaklı hale geliyor. Rota Maritime bu evrimle aktif olarak hizalanıyor: akıllı liman koordinasyon araçları; dijital belge akışları; gerçek zamanlı operasyonel izleme; gelişmiş müşteri iletişim sistemleri. Bu ileri görüşlü yaklaşım, Rota Yachting Agency'nin Türkiye'nin denizcilik ekosistemindeki inovasyonun ön saflarında kalmasını sağlıyor." },
        { h: "Beyond the Ordinary", p: "Türkiye'nin yat acentelik sektörü artık 'gelişmekte olan bir pazar' değil — küresel yatçılık ağının olgun, rekabetçi ve stratejik açıdan hayati bir bileşeni. Güçlü denizcilik mirası ve mükemmellik taahhüdüyle, Rota Maritime çatısı altındaki Rota Yachting Agency endüstri standartlarını yeniden tanımlamaya devam ediyor. Beyond the Ordinary sadece bir slogan değil — kesinlik, güven ve sürekli evrim üzerine kurulu bir hizmet felsefesini yansıtıyor." },
      ],
    },
  },
  {
    slug: "black-sea-yacht-entry-guide-sinop",
    date: "2026-02-15",
    readTime: 9,
    image: "/assets/photos/dji_fly_20230927_064938_342_1695786613786_photo_optimized.jpg",
    category: { en: "Regulations", ru: "Регулирование", tr: "Mevzuat" },
    en: {
      title: "Entering Türkiye from the Black Sea — why Sinop is the captain's perfect first port",
      excerpt: "Foreign-flag yachts arriving from Russia, Ukraine, Georgia, Bulgaria or Romania have one truly outstanding first port of call before the Bosphorus: Sinop. Here's why — and how Samsun and Amasra fit alongside.",
      body: [
        { h: "Sinop — the captain's perfect first port", p: "Sinop is, quite simply, the most beautifully placed natural harbour on the Turkish Black Sea coast. A horseshoe of high cliffs forms one of the world's largest natural amphitheatres of sheltered water — calm in any weather, deep enough for superyachts, and historically important since the 7th century BC. For Rota Yachting, Sinop is the home port. Our parent agency, Rota Denizcilik, was founded here in 1990 and our local relationships with the Harbour Master, Customs Directorate and pilot service span 35+ years. No other Black Sea entry combines this depth of local authority with such a stunning, naturally-protected anchorage." },
        { h: "Why Sinop wins on every operational dimension", p: "Shelter: a near-360° natural harbour, the only one of its kind on the Turkish Black Sea. Authority: Sinop Customs Directorate, Harbour Master and immigration handle Transit Logs, crew clearance and bonded stores in-house. Logistics: Sinop has its own airport (NOP) with daily İstanbul connections — crew rotations don't require a 4-hour drive to Samsun. Beauty: guests arriving from open Black Sea after 24–48 hours at sea step into a UNESCO-tentative-list mediaeval citadel, boutique fish restaurants on the citadel walls and clear turquoise water in front of the yacht. And — the local relationship dividend: Rota Yachting was born in Sinop. Every clearance, every supplier, every harbour authority is a 35-year handshake away." },
        { h: "Samsun — the 24/7 commercial backup", p: "Samsun is Türkiye's largest Black Sea port and runs customs, immigration and Border Police 24/7. It is the right call when: yachts arrive outside office hours; same-day duty-free bunker top-ups are needed; superyachts exceed Sinop's boutique scale; large-scale spare parts importation needs commercial customs handling; or crew rotations require Samsun Çarşamba Airport (SZF) seasonal European connections." },
        { h: "Amasra — the boutique western alternative", p: "Amasra is small, scenic and equally official. The Amasra Customs Directorate is fully empowered to issue Transit Logs and clear yachts. It is the right call for 30–45m yachts wanting a cultural overnight closer to the Bosphorus, or for itineraries continuing along the western Black Sea toward Karadeniz Ereğli." },
        { h: "The 7-step entry checklist (Sinop-led)", p: "1) Send the agent your ETA, last 5 ports of call, full crew list with passport scans, vessel registration and insurance certificates 48 hours in advance. 2) Agent files pre-arrival declarations with Sinop Harbour Master and Customs Directorate. 3) On arrival the agent boards alongside customs and immigration officers — typically inside Sinop's natural harbour. 4) Transit Log is issued (validity declared by captain, up to 1 year, renewable). 5) Crew passports stamped, bonded stores sealed if required. 6) Disbursement account is opened — agent settles port dues, customs fees and any local services on the yacht's behalf. 7) Yacht is officially cleared in. With proper pre-arrival paperwork, total dock time in Sinop is typically 60–90 minutes." },
        { h: "Common mistakes from the Black Sea", p: "Arriving without the previous port's outbound clearance certificate (mandatory). Declaring 'commercial' when the vessel is actually pleasure (or vice-versa). Forgetting to provide the bond / guarantee for crew on visa-on-arrival nationalities. Skipping the agent for cost reasons — saves €600 in agency fees, costs €4,000+ in detentions, fines and rebookings when something is wrong with the paperwork." },
        { h: "Onward to the Bosphorus", p: "Once cleared in at Sinop (or Samsun / Amasra), the yacht must still notify İstanbul Harbour Master 48 hours before Bosphorus transit and embark a strait pilot for vessels above 200 GT. Rota Yachting handles all of it: the entry clearance at Sinop, the optional intermediate stops at Samsun or Amasra, and the Bosphorus transit pre-arrival — from a single point of contact." },
        { h: "When to call us", p: "If your yacht is leaving any Black Sea port for Türkiye in the next 30 days, send your ETA and crew list now. We pre-file documents, reserve berth in Sinop's natural harbour, line up bunker quotes and have customs officers ready on the quay. WhatsApp +90 532 472 92 68 — answered 24/7 in English, Russian and Turkish." },
      ],
    },
    ru: {
      title: "Вход в Турцию из Чёрного моря — почему Синоп идеальный первый порт",
      excerpt: "Яхты под иностранным флагом, идущие из России, Украины, Грузии, Болгарии или Румынии, имеют один по-настоящему выдающийся первый порт захода до Босфора — Синоп. Вот почему — и как Самсун и Амасра дополняют его.",
      body: [
        { h: "Синоп — идеальный первый порт капитана", p: "Синоп — попросту самая красиво расположенная природная гавань на турецком побережье Чёрного моря. Подкова высоких скал образует один из крупнейших в мире природных амфитеатров защищённой воды — спокойной в любую погоду, достаточно глубокой для суперъяхт, исторически значимой с VII века до н. э. Для Rota Yachting Синоп — родной порт. Наше материнское агентство Rota Denizcilik было основано здесь в 1990 году, и наши местные отношения с капитанией, Таможней и лоцманской службой насчитывают более 35 лет. Ни один другой пункт входа на Чёрном море не сочетает такую глубину местного авторитета с такой потрясающе защищённой природой бухтой." },
        { h: "Почему Синоп выигрывает по всем параметрам", p: "Укрытие: почти 360° природная гавань, единственная такая на турецком Чёрном море. Авторитет: Таможенная дирекция Синопа, капитания и иммиграция оформляют Transit Log, экипаж и бондовые запасы на месте. Логистика: у Синопа собственный аэропорт (NOP) с ежедневными рейсами в Стамбул — смена экипажа не требует четырёхчасовой поездки до Самсуна. Красота: гости после 24–48 часов в открытом море попадают в средневековую цитадель ЮНЕСКО, бутик-рыбные рестораны на стенах и прозрачную бирюзовую воду перед яхтой. И — дивиденд локальных отношений: Rota Yachting родилась в Синопе. Каждое оформление, каждый поставщик, каждая портовая власть — это 35-летнее рукопожатие." },
        { h: "Самсун — круглосуточный коммерческий резерв", p: "Самсун — крупнейший черноморский порт Турции, таможня, иммиграция и пограничная полиция работают 24/7. Подходит, когда: яхты прибывают вне рабочих часов; нужна сразу бункеровка в режиме duty-free; суперъяхты превышают бутиковый масштаб Синопа; крупный ввоз запчастей через коммерческую таможню; или смена экипажа через аэропорт Самсун-Чаршамба (SZF) с сезонными европейскими рейсами." },
        { h: "Амасра — бутик-альтернатива на западе", p: "Амасра небольшая, живописная и так же официальная. Таможенная дирекция Амасры в полной мере уполномочена выдавать Transit Log и оформлять яхты. Подходит для 30–45 м яхт, которым нужен культурный заход ближе к Босфору, или для маршрутов вдоль западного Чёрного моря к Карадениз-Эрегли." },
        { h: "Чек-лист входа из 7 шагов (через Синоп)", p: "1) Передайте агенту ETA, последние 5 заходов, полный список экипажа со сканами паспортов, регистрационные документы и страховку за 48 часов. 2) Агент подаёт декларации в капитанию и Таможенную дирекцию Синопа. 3) По прибытии агент выходит на борт с таможней и иммиграцией — обычно внутри природной гавани Синопа. 4) Выдаётся Transit Log (срок до 1 года, продлеваемый). 5) Паспорта штампуются, бондовые запасы при необходимости опломбируются. 6) Открывается disbursement account — агент оплачивает портовые сборы и услуги от имени яхты. 7) Яхта официально оформлена. При правильной предварительной подаче — обычно 60–90 минут." },
        { h: "Частые ошибки со стороны Чёрного моря", p: "Приход без сертификата отхода предыдущего порта (обязателен). Декларация «коммерческой» при «прогулочной» эксплуатации (или наоборот). Забытая гарантия по экипажу с визой по прибытии. Отказ от агента ради экономии — экономит €600, стоит €4 000+ при ошибке." },
        { h: "Далее — Босфор", p: "После оформления в Синопе (или Самсуне / Амасре) яхта обязана уведомить капитанию Стамбула за 48 часов до транзита Босфора и взять лоцмана при GT свыше 200. Rota Yachting ведёт всё — оформление в Синопе, опциональные заходы в Самсун или Амасру, и предварительный транзит Босфора — из одной точки контакта." },
        { h: "Когда звонить нам", p: "Если ваша яхта выходит из любого черноморского порта в Турцию в ближайшие 30 дней — пришлите ETA и список экипажа уже сейчас. Мы подаём документы заранее, резервируем стоянку в природной гавани Синопа, собираем котировки на бункеровку и встречаем таможню на причале. WhatsApp +90 532 472 92 68 — 24/7 на английском, русском и турецком." },
      ],
    },
    tr: {
      title: "Karadeniz'den Türkiye'ye giriş — Sinop neden kaptanın mükemmel ilk limanı?",
      excerpt: "Rusya, Ukrayna, Gürcistan, Bulgaristan veya Romanya'dan gelen yabancı bayraklı yatların Boğaz öncesi gerçekten öne çıkan tek bir ilk yanaşma limanı vardır: Sinop. İşte nedeni — ve Samsun ile Amasra'nın yanında nasıl konumlandığı.",
      body: [
        { h: "Sinop — kaptanın mükemmel ilk limanı", p: "Sinop, basitçe söylemek gerekirse, Türkiye'nin Karadeniz kıyısındaki en güzel konumlandırılmış doğal limandır. Yüksek uçurumların oluşturduğu nal şeklindeki yapısı, dünyanın en büyük doğal korunaklı su amfilerinden birini meydana getirir — her havada sakin, süperyatlar için yeterince derin, MÖ 7. yüzyıldan beri tarihsel açıdan önemli. Rota Yachting için Sinop ana limandır. Ana acentemiz Rota Denizcilik 1990 yılında burada kuruldu; Liman Başkanlığı, Gümrük Müdürlüğü ve kılavuz kaptan servisiyle yerel ilişkilerimiz 35 yılı aşkın bir zamana yayılır. Karadeniz'deki başka hiçbir giriş, bu yerel otorite derinliğini bu denli muhteşem ve doğal olarak korunaklı bir koyla birleştirmez." },
        { h: "Sinop her operasyonel boyutta neden öne geçiyor?", p: "Korunma: Türkiye Karadeniz'inde tek olan, neredeyse 360° doğal liman. Otorite: Sinop Gümrük Müdürlüğü, Liman Başkanlığı ve göçmenlik Transit Log, mürettebat çıkışı ve bonded stores işlemlerini kendi bünyesinde yürütür. Lojistik: Sinop'un kendi havalimanı (NOP) İstanbul'a günlük seferlerle bağlıdır — mürettebat değişimi için Samsun'a 4 saatlik yolculuk gerekmez. Güzellik: 24-48 saatlik açık deniz geçişinden sonra konuklar UNESCO geçici listesindeki ortaçağ kalesi, kale duvarlarındaki butik balık restoranları ve yat önündeki berrak turkuaz suyla karşılaşır. Ve — yerel ilişki temettüsü: Rota Yachting Sinop'ta doğdu. Her çıkış, her tedarikçi, her liman otoritesi 35 yıllık bir el sıkışma uzaklığındadır." },
        { h: "Samsun — 7/24 ticari yedek", p: "Samsun, Türkiye'nin Karadeniz kıyısındaki en büyük limanıdır; gümrük, göçmenlik ve sınır polisi 7/24 görev yapar. Şu durumlarda doğru tercihtir: mesai dışı varan yatlar; aynı gün duty-free bunker takviyesi gereken yatlar; Sinop'un butik ölçeğini aşan süperyatlar; ticari gümrük gerektiren büyük yedek parça ithalatı; veya Samsun Çarşamba Havalimanı (SZF) sezonluk Avrupa bağlantılarını gerektiren mürettebat değişimleri." },
        { h: "Amasra — batıda butik alternatif", p: "Amasra küçük, manzaralı ve aynı oranda resmidir. Amasra Gümrük Müdürlüğü Transit Log düzenleme ve yat çıkışı için tam yetkilidir. Boğaz'a daha yakın kültürel bir gecelik isteyen 30–45 m yatlar veya batı Karadeniz boyunca Karadeniz Ereğli'ye devam eden rotalar için doğru seçim." },
        { h: "7 adımlı giriş kontrol listesi (Sinop merkezli)", p: "1) Acenteye ETA, son 5 liman, pasaport taramalı tam mürettebat listesi, gemi tescili ve sigorta sertifikalarını 48 saat önceden gönderin. 2) Acente Sinop Liman Başkanlığı ve Gümrük Müdürlüğü'ne geliş öncesi beyannameleri verir. 3) Varışta acente gümrük ve göçmenlik memurlarıyla yat üstüne çıkar — genellikle Sinop'un doğal limanı içinde. 4) Transit Log düzenlenir (kaptan tarafından beyan edilen süre, 1 yıla kadar uzatılabilir). 5) Pasaportlar mühürlenir, gerekirse bonded stores mühürlenir. 6) Disbursement account açılır — acente liman harçlarını, gümrük ücretlerini ve yerel hizmetleri yat adına öder. 7) Yat resmi olarak içeri alınmıştır. Doğru ön evrakla Sinop'ta toplam iskele süresi tipik olarak 60–90 dakikadır." },
        { h: "Karadeniz tarafından sık yapılan hatalar", p: "Bir önceki limanın çıkış sertifikası olmadan gelmek (zorunlu). Aslında pleasure olan tekneyi 'ticari' (veya tersi) beyan etmek. Vize-on-arrival uyrukları için mürettebat teminatı / garantisini unutmak. Maliyet için acenteyi atlamak — €600 acentelik ücreti tasarruf ettirir, evrakta bir hata çıktığında €4.000+ gözaltı, ceza ve yeniden rezervasyona mal olur." },
        { h: "Sonraki adım: Boğaz", p: "Sinop'ta (veya Samsun / Amasra'da) çıkışı tamamlanan yat, Boğaz transitinden 48 saat önce İstanbul Liman Başkanlığı'na bildirim yapmak ve 200 GT üzeri tekneler için kılavuz kaptan almak zorundadır. Rota Yachting hepsini yürütür: Sinop'ta giriş çıkışı, Samsun veya Amasra'daki opsiyonel ara duraklar ve Boğaz transit ön hazırlığı — tek temas noktasından." },
        { h: "Bizi ne zaman arayın", p: "Yatınız önümüzdeki 30 gün içinde herhangi bir Karadeniz limanından Türkiye'ye çıkacaksa, ETA ve mürettebat listenizi şimdi gönderin. Belgeleri önceden veririz, Sinop'un doğal limanında yer ayırtırız, bunker tekliflerini toplar ve gümrük memurlarını rıhtımda hazır bekleriz. WhatsApp +90 532 472 92 68 — 7/24 İngilizce, Rusça ve Türkçe yanıtlanır." },
      ],
    },
  },
  {
    slug: "yacht-clearance-turkey-guide",
    date: "2026-01-14",
    readTime: 7,
    image: "/assets/photos/stock-794c66072133.png",
    category: { en: "Regulations", ru: "Регулирование" },
    en: {
      title: "How yacht clearance works in Türkiye",
      excerpt: "A practical guide to arrival and departure formalities, the Transit Log, and what separates a smooth call from a 72-hour delay.",
      body: [
        { h: "The one-page summary", p: "Every foreign-flagged yacht entering Turkish waters must clear in at an official port of entry, obtain a Transit Log, and clear out before leaving. Turkish-flagged yachts have simpler but still specific formalities. The paperwork is not optional and is enforced by Harbour Masters, Customs and the Coast Guard." },
        { h: "Ports of entry", p: "Istanbul (various), Çanakkale, İzmir, Çeşme, Kuşadası, Bodrum, Marmaris, Göcek, Fethiye, Finike, Antalya, Alanya, and others. A yacht cannot legally enter Turkish waters at a non-entry port." },
        { h: "The Transit Log", p: "Issued by the Harbour Master, valid for the duration declared (up to one year, renewable). It lists the yacht, crew, intended movements and must be stamped at each successive port. Losing or failing to present the Transit Log can halt onward movement." },
        { h: "Crew and guest formalities", p: "All crew and guests require passport entry stamps. Crew on foreign-flagged yachts may require seaman's books or visas depending on nationality. Immigration officers attend the yacht alongside the agent on first arrival." },
        { h: "What captains most often get wrong", p: "Declaring 'pleasure' when chartering, arriving outside office hours without pre-notification, presenting outdated insurance certificates, or forgetting to clear out before leaving Turkish waters. Each mistake is fixable — but each costs time." },
        { h: "What an agent actually does", p: "A licensed Turkish ship agent (Rota Yachting holds Licence No. 0466) files declarations in advance, attends the vessel with authorities on arrival, issues the Transit Log, manages port dues, and clears the yacht out on departure. The captain signs once. The agent runs the rest." },
      ],
    },
    ru: {
      title: "Как устроено оформление яхт в Турции",
      excerpt: "Практическое руководство по формальностям прихода и отхода, Transit Log и тому, что отличает гладкий заход от 72-часовой задержки.",
      body: [
        { h: "Краткое резюме", p: "Каждая иностранная яхта, входящая в турецкие воды, должна оформиться в официальном порту входа, получить Transit Log и оформиться на выход до убытия. У турецких яхт формальности проще, но тоже специфичны. Эти бумаги — не опция, а требование, которое применяют капитания, таможня и береговая охрана." },
        { h: "Порты входа", p: "Стамбул (разные), Чанаккале, Измир, Чешме, Кушадасы, Бодрум, Мармарис, Гёджек, Фетхие, Финике, Анталья, Аланья и другие. Яхта не может законно войти в турецкие воды в неуполномоченном порту." },
        { h: "Transit Log", p: "Выдаётся капитанией, действует на заявленный срок (до года с продлением). В нём — яхта, экипаж, маршрут; документ штампуется в каждом следующем порту. Потеря или непредъявление Transit Log останавливает движение." },
        { h: "Формальности экипажа и гостей", p: "Всем — отметка о въезде в паспорте. Экипажу на иностранных яхтах в зависимости от гражданства могут понадобиться мореходные книжки или визы. Иммиграция выезжает на борт вместе с агентом при первом заходе." },
        { h: "Чаще всего капитаны ошибаются в следующем", p: "Заявляют «удовольствие» при чартере, приходят вне рабочих часов без предуведомления, подают просроченные страховые сертификаты, забывают оформиться на выход. Всё поправимо — но каждое стоит времени." },
        { h: "Что реально делает агент", p: "Лицензированный турецкий судовой агент (лицензия Rota Yachting № 0466) подаёт декларации заранее, выходит на борт с властями, выдаёт Transit Log, ведёт портовые сборы и оформляет яхту на выход. Капитан подписывает один раз. Остальное ведёт агент." },
      ],
    },
  },
  {
    slug: "bodrum-superyacht-marinas",
    date: "2025-12-10",
    readTime: 6,
    image: "/assets/photos/stock-luxury-marina-sunset.jpg",
    category: { en: "Destinations", ru: "Направления" },
    en: {
      title: "Best marinas in Bodrum for superyachts",
      excerpt: "Yalıkavak, Turgutreis, Bodrum Milta and D-Marin — a practical comparison of capacity, draft, services and booking windows.",
      body: [
        { h: "Yalıkavak Marina", p: "The Aegean benchmark. 400+ berths, up to 140m LOA, draft up to 8m. Palmarina superyacht quay, full shipyard capability, helipad, concierge. Peak season (mid-July to late August) is fully booked by April — priority berths are negotiated via agency channels." },
        { h: "D-Marin Turgutreis", p: "Large capacity, ISPS secure, good for 30–60m. Excellent for charter turnarounds with Bodrum airport 35 minutes away. Fuel dock, chandlery, car hire on site." },
        { h: "Milta Bodrum Marina", p: "Closest to Bodrum town centre. Up to 60m LOA. Ideal when guests want walking access to restaurants and nightlife." },
        { h: "Bodrum commercial port", p: "Used by the largest yachts for ceremonial stops. Agency attendance mandatory — Harbour Master must authorise alongside." },
        { h: "Booking reality", p: "August berths in Yalıkavak are effectively allocated the previous November. May, June and September remain available at reasonable notice. Winter lay-up rates in the Bodrum peninsula are competitive against Italy and Spain." },
      ],
    },
    ru: {
      title: "Лучшие марины Бодрума для суперъяхт",
      excerpt: "Ялыкавак, Тургутрейс, Бодрум Милта и D-Marin — практическое сравнение вместимости, осадки, сервисов и окон бронирования.",
      body: [
        { h: "Yalıkavak Marina", p: "Эталон Эгейки. 400+ мест, LOA до 140 м, осадка до 8 м. Суперъяхтенный причал Palmarina, полноценная верфь, вертолётная площадка, консьерж. Пик сезона (середина июля — конец августа) полностью забронирован к апрелю — приоритетные места идут через агентские каналы." },
        { h: "D-Marin Turgutreis", p: "Высокая вместимость, ISPS-защита, хорошо подходит для 30–60 м. Отлично для чартерных оборотов — аэропорт Бодрума в 35 минутах. Топливный причал, чандлер, аренда авто на месте." },
        { h: "Milta Bodrum Marina", p: "Ближе всех к центру Бодрума. LOA до 60 м. Идеально, когда гостям нужен пешеходный доступ к ресторанам и ночной жизни." },
        { h: "Коммерческий порт Бодрума", p: "Используется крупнейшими яхтами для церемониальных заходов. Присутствие агента обязательно — швартовку авторизует капитания." },
        { h: "Реальность бронирования", p: "Августовские места в Ялыкавак фактически расписываются в предыдущем ноябре. Май, июнь и сентябрь доступны при разумном уведомлении. Ставки зимнего лей-апа на полуострове Бодрума конкурентны Италии и Испании." },
      ],
    },
  },
  {
    slug: "provisioning-guide-turkey",
    date: "2025-11-02",
    readTime: 5,
    category: { en: "Operations", ru: "Операции" },
    en: {
      title: "Yacht provisioning guide: Türkiye",
      excerpt: "What to expect from Turkish provisioning — quality, sourcing, customs, and the unspoken rules of charter turnarounds.",
      body: [
        { h: "Local quality is genuinely high", p: "Turkish produce, dairy, seafood and butchery are world-class. Fresh local sourcing usually outperforms imported equivalents on quality and price — in Bodrum, Göcek and Istanbul especially." },
        { h: "Imported premium is available", p: "French wines, Italian truffles, Japanese wagyu — all reliably sourced via bonded importers. Lead times of 24–72 hours for non-stocked premium items are standard." },
        { h: "Bonded (duty-free) stores", p: "Foreign-flagged yachts under Transit Log qualify for bonded wine, spirits and tobacco. Bonded stores must be sealed on arrival, released on departure, and properly documented. Agency handling is required." },
        { h: "Charter turnaround timings", p: "Saturday turnaround: provisioning list typically delivered by Wednesday evening, final delivery Friday afternoon, last adjustments Saturday morning before guest arrival. We work to those timings by default." },
        { h: "What to avoid", p: "Assuming small quantities can be purchased at retail and reimbursed — this rarely works under yacht regime. Use an agency-run provisioning invoice from the start." },
      ],
    },
    ru: {
      title: "Руководство по снабжению яхты: Турция",
      excerpt: "Чего ждать от турецкого снабжения — качество, источники, таможня и негласные правила чартерных оборотов.",
      body: [
        { h: "Локальное качество действительно высокое", p: "Турецкие фрукты и овощи, молочка, морепродукты и мясо — мирового уровня. Свежие местные источники обычно выигрывают у импорта по качеству и цене — особенно в Бодруме, Гёджеке и Стамбуле." },
        { h: "Импортный премиум доступен", p: "Французские вина, итальянские трюфели, японский вагю — надёжно через бондовых импортёров. Сроки 24–72 часа для несклад­ских премиум-позиций — норма." },
        { h: "Бондовые (беспошлинные) запасы", p: "Иностранные яхты с Transit Log имеют право на бондовые вина, крепкие алкоголи и табак. Запасы пломбируются при входе, распечатываются при отходе, должным образом оформляются. Агентское сопровождение обязательно." },
        { h: "Тайминги чартерного оборота", p: "Субботний оборот: лист снабжения обычно в среду вечером, финальная доставка — пятница после полудня, последние правки — утро субботы до прибытия гостей. По умолчанию мы работаем в этих таймингах." },
        { h: "Чего избегать", p: "Не предполагайте, что малые партии можно купить в рознице и вернуть деньгами — в режиме яхты это редко работает. С начала используйте агентский счёт на снабжение." },
      ],
    },
  },
  {
    slug: "turkish-waters-cruising-regulations",
    date: "2025-09-18",
    readTime: 8,
    category: { en: "Regulations", ru: "Регулирование" },
    en: {
      title: "Cruising regulations in Turkish waters",
      excerpt: "What every captain should know before a Turkish season — charter rules, marine parks, the blue card and recent regulatory changes.",
      body: [
        { h: "The cabotage question", p: "Only Turkish-flagged yachts may legally charter between Turkish ports. Foreign-flagged yachts cruising with owners and non-paying guests operate freely. The distinction matters — and is enforced." },
        { h: "Blue card (Mavi Kart)", p: "All yachts — Turkish or foreign — discharging black water must use certified pump-out facilities and carry a blue card recording pumps. Random inspections occur, especially around Göcek and Fethiye." },
        { h: "Marine parks and SPAs", p: "Gulf of Göcek, Fethiye Bay and parts of the Datça peninsula are Specially Protected Areas. Anchoring restrictions apply to protect Posidonia meadows; designated buoyed zones exist." },
        { h: "Night navigation", p: "Permitted but inspected. Coast Guard boardings are more frequent at night in the Datça–Bozburun corridor. Paperwork should be instantly producible." },
        { h: "Environmental fines", p: "Discharging waste in protected waters draws fines that escalate quickly. Pump-out ashore is the safe protocol and is easily coordinated through the agency." },
        { h: "What changes season to season", p: "Marine park boundaries, SSR rules and local Harbour Master interpretations can shift each April. A good agency briefing before the season starts prevents nearly all surprises." },
      ],
    },
    ru: {
      title: "Регулирование круизов в турецких водах",
      excerpt: "Что следует знать капитану перед турецким сезоном — правила чартера, морские парки, синяя карта и последние изменения.",
      body: [
        { h: "Вопрос каботажа", p: "Только яхты под турецким флагом могут легально работать в чартере между турецкими портами. Иностранные яхты с владельцем и неплатящими гостями ходят свободно. Различие принципиально — и контролируется." },
        { h: "Синяя карта (Mavi Kart)", p: "Все яхты — турецкие и иностранные — при сбросе чёрной воды обязаны использовать сертифицированные станции откачки и вести синюю карту. Выборочные проверки происходят, особенно в Гёджеке и Фетхие." },
        { h: "Морские парки и особо охраняемые зоны", p: "Залив Гёджек, бухта Фетхие и части полуострова Датча — особо охраняемые территории. Действуют ограничения на якорение ради защиты лугов посидонии; есть буйковые зоны." },
        { h: "Ночное плавание", p: "Разрешено, но контролируется. Проверки береговой охраны ночью чаще в коридоре Датча–Бозбурун. Документы должны быть под рукой." },
        { h: "Экологические штрафы", p: "Сброс отходов в охраняемых водах карается быстро растущими штрафами. Откачка на берегу — безопасный протокол, легко координируется через агентство." },
        { h: "Что меняется от сезона к сезону", p: "Границы морских парков, правила SSR и трактовки местных капитаний меняются каждым апрелем. Хороший агентский бриф до сезона предотвращает почти все сюрпризы." },
      ],
    },
  },
];

export const officeAddresses = {
  en: {
    head: {
      label: "Head Office · Sinop",
      lines: [
        "Meydankapı Mah. Denizciler Yolu",
        "Rota Business Center No: 1/1-2",
        "Sinop, Türkiye",
      ],
    },
    istanbul: {
      label: "Branch Office · İstanbul",
      lines: [
        "Kanuni Cad. Nef Sancaktepe",
        "E Blok Daire 7",
        "İstanbul, Türkiye",
      ],
    },
    amasra: {
      label: "Branch Office · Amasra",
      lines: [
        "Kaleşah Mah. Vadi Evleri Sitesi No: 25",
        "Amasra / Bartın, Türkiye",
      ],
    },
  },
  ru: {
    head: {
      label: "Головной офис · Синоп",
      lines: [
        "Meydankapı Mah. Denizciler Yolu",
        "Rota Business Center No: 1/1-2",
        "Синоп, Турция",
      ],
    },
    istanbul: {
      label: "Филиал · Стамбул",
      lines: [
        "Kanuni Cad. Nef Sancaktepe",
        "E Blok Daire 7",
        "Стамбул, Турция",
      ],
    },
    amasra: {
      label: "Филиал · Амасра",
      lines: [
        "Kaleşah Mah. Vadi Evleri Sitesi No: 25",
        "Амасра / Бартын, Турция",
      ],
    },
  },
};

export const companyDetails = {
  legalName: "Rota Denizcilik Turizm ve Dış Ticaret A.Ş.",
  mersis: "0735002496900016",
  taxId: "7350024969",
  tradeRegistry: "760-Sinop",
  agencyLicense: "0466",
};

export const contactInfo = {
  whatsapp: "905324729268",
  whatsappDisplay: "+90 532 472 92 68",
  email: "yachting@rotayachting.com.tr",
  phone: "+90 368 261 88 74",
  mobileBurhan: "+90 532 472 92 68",
  mobileSitki: "+90 532 348 22 18",
  instagram: "rotamaritime",
  instagramUrl: "https://instagram.com/rotamaritime",
  mapEmbed: "https://storage.googleapis.com/maps-solutions-2xwo312vuw/locator-plus/mnt7/locator-plus.html",
};

export const media = {
  logo: "/assets/brand/beyond-the-ordinary.png",
  heroMain: "/assets/photos/stock-83cb536f88ae.png",
  texture: "/assets/photos/stock-977e6ff0aa67.png",
  crewOps: "/assets/photos/stock-e753596af852.png",
  turkishCoast: "/assets/photos/stock-794c66072133.png",
  marinaSunset: "/assets/photos/stock-luxury-marina-sunset.jpg",
};

// ---------- Auto-fallback for missing translations ----------
// Recursively copies `en` content into `tr`/`ru` fields where they are missing,
// so the UI never crashes when a translation hasn't been authored yet.
const _addLangFallback = (obj, langs = ["tr", "ru"]) => {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    obj.forEach((item) => _addLangFallback(item, langs));
    return;
  }
  langs.forEach((lng) => {
    if (obj.en !== undefined && obj[lng] === undefined) {
      obj[lng] = obj.en;
    }
  });
  Object.values(obj).forEach((v) => {
    if (v && typeof v === "object") _addLangFallback(v, langs);
  });
};

_addLangFallback(services);
_addLangFallback(destinations);
_addLangFallback(articles);
_addLangFallback(officeAddresses);

// ---------- Manual TR translations (override fallback) ----------
const TR_SERVICES = {
  "port-clearance-transit-log": {
    name: "Liman Çıkışı ve Transit Log",
    short: "Türk yat mevzuatına göre tam giriş, transit ve çıkış işlemleri — yabancı bayraklı yatlar için Transit Log dahil.",
    intro: "Liman çıkışı, Türkiye'ye gelen her yat yanaşmasının evrak temelidir. Rota Yachting, Türk sularında seyreden yabancı bayraklı yatlar için zorunlu Transit Log dahil tüm geliş/gidiş formalitelerini anahtar teslim üstlenir.",
    handles: [
      "Liman Başkanlığına geliş öncesi bildirimler",
      "Transit Log düzenleme ve süre uzatma",
      "Mürettebat ve konuk varış/ayrılış formaliteleri",
      "Gemi belgelerinin doğrulanması",
      "Liman ve fener harçları, disbursement account",
      "Liman Başkanlığı, gümrük ve sahil güvenlikle irtibat",
    ],
    process: [
      "ETA, yat belgeleri ve mürettebat listesini alıyoruz",
      "Ön beyannameleri elektronik olarak veriyoruz",
      "Varışta yetkililerle birlikte yat üstüne çıkıyoruz",
      "Transit Log ve çıkış belgelerini düzenliyoruz",
      "Ayrılışta nihai disbursement account'u teslim ediyoruz",
    ],
    why: "Çıkış evrakındaki hatalar gözaltı, ceza ve karaliste sebebidir. Acentemiz lisanslıdır (Lisans No. 0466) ve belgeleri yat adına verir — kaptan hiçbir zaman gişeye gelmek zorunda kalmaz.",
  },
  "berth-reservations": {
    name: "Yer Ayırtma ve Liman Koordinasyonu",
    short: "Türkiye'nin önde gelen marina ve ticari limanlarında öncelikli yer tahsisi — sezon zirvesi dahil.",
    intro: "Ağustosta Yalıkavak'ta bir hafta veya Göcek'te sessiz bir kışlama isterseniz, Türkiye'deki marina operatörleriyle ilişkilerimiz, açık kanallarda bulunmayan yerleri sağlar.",
    handles: [
      "Yüksek sezonda önceden yer ayırtma",
      "Son dakika yer bulma ve değiş-tokuş",
      "Marina sözleşmeleri, fiyat müzakeresi, faturalama",
      "Karadan elektrik, su ve yanaşma desteği",
      "Sezonluk kışlama planlaması (kış / yaz)",
      "Süperyatlar için ticari liman yanaşması",
    ],
    process: [
      "LOA, en, çekim ve tercih edilen tarihleri paylaşırsınız",
      "Partner marina ağımıza dağıtırız",
      "Müsaitlik ve ücretleri yazılı onaylarız",
      "Depozito veya garantiyle rezervasyonu sabitleriz",
      "Varışı dockmaster ile koordine ederiz",
    ],
    why: "Bodrum, Göcek ve Marmaris'te zirve sezon yerleri neredeyse tamamı önceden rezervedir. Acente ilişkilerimiz, doğrudan başvuruların açamadığı kapıları açar.",
  },
  "customs-immigration": {
    name: "Gümrük ve Göçmenlik",
    short: "Gümrük beyannamesi, bonded stores, mürettebat vizeleri ve göçmenlik çıkışı — Türk mevzuatı çerçevesinde.",
    intro: "Yatın geçici ithalatından mürettebat göçmenliğine kadar tüm gümrük ve sınır formaliteleri lisanslı Türk gümrük müşavirleri ve göçmenlik irtibatlarıyla içeride yönetilir.",
    handles: [
      "Yat rejimi altında geçici ithalat",
      "Bonded stores mühürleme ve serbest bırakma",
      "Mürettebat vize rehberliği ve ikamet izinleri",
      "Özel gelişlerde konuk göçmenlik",
      "Yedek parça ve teknik kalemlerin ithalatı",
      "KDV rehberliği ve ticari yat rejimleri",
    ],
    process: [
      "Yat ve mürettebat belgelerini önceden veriyoruz",
      "Gümrük memurlarını yat üstüne koordine ediyoruz",
      "Gerekirse bonded stores'u mühürlüyoruz",
      "Konuk ve mürettebat pasaport formalitelerini hallediyoruz",
      "Ayrılışta çıkış işlemlerini düzenliyoruz",
    ],
    why: "Türk gümrük ve göçmenlik mevzuatı spesifiktir ve aktif olarak uygulanır. Tek bir gözden kaçan form 48-72 saat hareketi durdurabilir. Bunu engelleriz.",
  },
  "bunkering": {
    name: "Bunker Koordinasyonu",
    short: "Türk limanlarında rekabetçi gümrüksüz ve gümrüklü bunker tedariki, kalite numune alma ve teslim denetimi.",
    intro: "Türkiye'de bunker fiyatları limanlar, tedarikçiler ve rejimler arasında kayda değer farklılık gösterir. Tedarikçiyi buluruz, fiyat alırız, nominasyon yapar ve teslimi denetleriz — yatı doğru numune ve belgeyle koruruz.",
    handles: [
      "Yat rejimi altında gümrüksüz bunker teslimi",
      "Birden fazla Türk tedarikçiden teklif",
      "Nominasyon, programlama ve hazır bulunma",
      "Mavna ve hat teslimi koordinasyonu",
      "Numune alma ve kalite uyuşmazlıkları",
      "Bunker Delivery Note ve mutabakat",
    ],
    process: [
      "Miktar, derece ve tarih alırız",
      "Onaylı tedarikçilere dağıtırız",
      "Kaptana karşılaştırmalı teklif veririz",
      "Nominasyon yapar ve programa alırız",
      "Hazır bulunur, numuneye şahit olur, BDN imzalarız",
    ],
    why: "Kötü bir bunker yatı haftalarca devre dışı bırakır. Doğru nominasyon, hazır bulunma ve numune alma; tasarruf ile felaket arasındaki farktır.",
  },
  "provisioning": {
    name: "İkmal ve Lojistik",
    short: "Yiyecek-içecek, çiçek, çamaşır, helikopter ve özel jet lojistiği — Türkiye genelinde aynı standartta sağlanır.",
    intro: "Premium peynir, sezonluk balık, taze ekmek, en sevdiği şarap evi — yat ne istiyorsa onu sahanın en iyi tedarikçilerinden temin eder, denetler ve teslim ederiz.",
    handles: [
      "Premium yat ikmali ve B2B kuru depo",
      "Çamaşır, kuru temizleme, üniforma",
      "Taze çiçek ve dekor",
      "Charter konuk hediye paketleri",
      "Karadan ekspres kurye ve gümrük",
      "Helikopter, jet ve kara ulaşımı",
    ],
    process: [
      "Mürettebat / yat yöneticisinden liste alırız",
      "Onaylı tedarikçi panelinde yer ayırırız",
      "Numune ve teklif kaptana onay için sunarız",
      "Teslim sırasında bizzat hazır bulunuruz",
      "İmzalı belge ve faturayı düzenleriz",
    ],
    why: "Yat ikmali standart catering değildir — mütevazı bir kahvaltıda fark, doğru bir yat acentesinin görünmez işidir.",
  },
  "crew-guest-logistics": {
    name: "Mürettebat ve Konuk Lojistiği",
    short: "Vize, ikamet izni, sertifika doğrulama, havalimanı transferleri ve konaklama — mürettebatın tüm yer ihtiyaçları için.",
    intro: "Mürettebat değişimleri, vize uzatmaları, MCA / STCW belgesi yenileme süreçleri ve havalimanı koordinasyonu — hepsi tek noktadan yönetilir.",
    handles: [
      "Türk vizesi başvuru desteği",
      "Mürettebat ikamet izni",
      "Mürettebat değişim lojistiği",
      "Havalimanı VIP karşılama (IST, SAW, DLM, ADB, SZF, NOP)",
      "Konaklama ve oteller",
      "Sertifika yenileme koordinasyonu",
    ],
    process: [
      "Mürettebat manifestosu ve uçuş bilgilerini paylaşırsınız",
      "Vize ve evrak gereksinimlerini doğrularız",
      "Havalimanında karşılama ekibi atarız",
      "Yata transferi koordine ederiz",
      "Belge süreçlerini takip ederiz",
    ],
    why: "Mürettebat lojistiği ihmal edildiğinde charter takvimleri çöker. Süper bir yat, son dakika değişen üçüncü kaptanı kabul edemez.",
  },
  "technical-support": {
    name: "Teknik Destek",
    short: "Yedek parça ithalatı, tersane koordinasyonu, paint refit, klas ve bayrak denetim hazırlığı.",
    intro: "Türkiye, ciddi tersane kapasitesine sahiptir — Tuzla, Antalya ve Bodrum'da. Acente olarak teknik tedarik zincirini, gümrük çıkışını ve klas / bayrak gereksinimlerini koordine ederiz.",
    handles: [
      "Yedek parça ithalatı ve gümrük çıkışı",
      "Refit / paint tersane koordinasyonu",
      "Klas (LRS, BV, ABS, RINA) denetim hazırlığı",
      "Bayrak devleti denetim koordinasyonu",
      "Teknisyen seyahati ve karada barınma",
      "Teknik dosya yönetimi",
    ],
    process: [
      "Yat yöneticisi ihtiyaç listesini gönderir",
      "Türk tersanelerinden teklif toplarız",
      "Karşılaştırmalı tablo ve teknik notları sunarız",
      "İlerlemeyi haftalık raporlarla takip ederiz",
      "Tamamlandığında yat sahaya çıkar",
    ],
    why: "Türk tersane fiyatları Avrupa'ya göre %30-40 daha avantajlıdır — ancak yalnızca acente koordinasyonu yedek parça ithalatı, klas denetimi ve bayrak uyumluluğunu eksiksiz tutarsa.",
  },
  "concierge": {
    name: "Concierge ve Yerel Destek",
    short: "Restoran, helikopter, özel jet, otel, şoför, güvenlik, etkinlik — yatın etrafındaki her şey.",
    intro: "Yatın bordasında biten yerde Türkiye'nin en iyi servisleri başlar — biz ikisini görünmez biçimde birleştiririz. Restoranlar, müzeler, helikopterler, kişisel şoförler — hepsi tek isim altında.",
    handles: [
      "Premium restoran rezervasyonları",
      "Özel jet ve helikopter koordinasyonu",
      "Beş yıldızlı otel paketleri",
      "Güvenlik personeli ve şoför",
      "Özel etkinlik ve davetler",
      "Müze, antik alan ve tur özelleştirmeleri",
    ],
    process: [
      "Konuk profilinizi paylaşırsınız",
      "Tercihler doğrultusunda program hazırlarız",
      "Önbilgili VIP karşılama partnerleri ayarlarız",
      "Anlık değişiklikleri çözmek için 7/24 hattayız",
      "Sonradan geri bildirim raporu sunarız",
    ],
    why: "Türkiye'de iyi bir akşam yemeği rezervasyonu ile mükemmel bir gece arasındaki fark, doğru numarayı bilmektir — biz biliriz.",
  },
  "cruising-support": {
    name: "Türkiye'de Seyir Desteği",
    short: "Rota tasarımı, demirleme zekası, hava rotası ve çoklu liman kapsamı — Türk seyirleri için.",
    intro: "Tek bir limanda kalmıyorsanız: 7-21 günlük rotalar, demirleme planları, alternatif sığınaklar, çoklu liman acente koordinasyonu — hepsini birden tek aramada çözeriz.",
    handles: [
      "Rota tasarımı (3-21 gün)",
      "Demirleme zekası ve sığınak haritası",
      "Hava rotası ve plan B'leri",
      "Çoklu liman acente devir-teslim",
      "Kıyıdan keşif ve sahada gözlem",
      "Charter konuk programı entegrasyonu",
    ],
    process: [
      "Yat profilini ve konuk ihtiyaçlarını paylaşırsınız",
      "Rota taslağını ve alternatifleri sunarız",
      "Onay sonrası tüm liman acentelerini koordine ederiz",
      "Seyrin canlı operasyonel desteği",
      "Tamamlandığında özetli rapor",
    ],
    why: "Türk seyrinin sırrı, tek limana gömülmemek değildir — birden fazla limanı zahmetsiz görünmesini sağlamaktır. Bunu biz yaparız.",
  },
};

const TR_DESTINATIONS = {
  "istanbul": {
    name: "İstanbul",
    tagline: "Karadeniz'i Marmara, Ege ve Akdeniz'e bağlayan denizcilik kapısı.",
    intro: "İstanbul, Türkiye'nin en stratejik yat duraklarından biridir. Boğaz geçişi, premium marinaları, beş yıldızlı otel/restoran erişimi ve Sabiha Gökçen / İstanbul Havalimanı özel jet lojistiği şehri uluslararası bir hub yapar.",
  },
};

services.forEach((s) => {
  if (TR_SERVICES[s.slug]) s.tr = TR_SERVICES[s.slug];
});
destinations.forEach((d) => {
  if (TR_DESTINATIONS[d.slug]) d.tr = TR_DESTINATIONS[d.slug];
});
